// frontend/src/components/portfolio/BlockFormBuilder.tsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Slider,
  Typography,
  Button,
  FormHelperText,
  Card,
  CardMedia,
  IconButton,
  Grid,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  CircularProgress,
  DialogContent,
  List,
  ListItem,
  DialogActions,
  ListItemText
} from '@mui/material';
import {
  Image as ImageIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { BlockRegistryEntry } from '../../types/Portfolio';
import MediaPicker from './MediaPicker';
import RichTextEditor from './RichTextEditor';
import { METRIC_ICONS, CATEGORY_LABELS, getIconsByCategory } from '../../constants/metricIcons';
import apiClient from '../../services/apiClient';


interface BlockFormBuilderProps {
  blockType: string;
  blockData: any;
  editFields: BlockRegistryEntry['editFields'];
  onChange: (data: any) => void;
}

const arrayFieldNames = [
  'metrics',
  'offerings',
  'cases',
  'testimonials',
  'members',
  'advisors',
  'fields',
  'mediaIds',
  'values',
  'images',
  'featuredArtists',
  'ageDistribution',
  'professionalProfile',
  'geographicReach',
  'socialMedia',
  'pressCoverage',
  'categories',
  'videos',
  'socialLinks',
  'resourceLinks',
  'programs',
];

const BlockFormBuilder: React.FC<BlockFormBuilderProps> = ({
  blockType,
  blockData,
  editFields,
  onChange
}) => {
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false);
  const [currentImageField, setCurrentImageField] = useState<string | null>(null);
  const [artistSearchOpen, setArtistSearchOpen] = useState(false);
  const [artistSearch, setArtistSearch] = useState('');
  const [artistResults, setArtistResults] = useState<any[]>([]);
  const [loadingArtists, setLoadingArtists] = useState(false);
  const [selectedArtists, setSelectedArtists] = useState<any[]>([]);

  useEffect(() => {
  if (blockData.artistIds && blockData.artistIds.length > 0) {
    fetchSelectedArtists(blockData.artistIds);
  }
}, [blockData.artistIds]);

const fetchSelectedArtists = async (ids: string[]) => {
  try {
    const artists = await apiClient.post<any[]>('/api/bandrecommendations/batch', { ids });
    setSelectedArtists(artists);
  } catch (error) {
    console.error('Error fetching selected artists:', error);
  }
};


  const handleFieldChange = (fieldName: string, value: any) => {
    onChange({
      ...blockData,
      [fieldName]: value
    });
  };

  const handleImagePickerOpen = (fieldName: string) => {
    setCurrentImageField(fieldName);
    setMediaPickerOpen(true);
  };

const handleImageSelect = (imageUrl: string) => {
  if (currentImageField) {
    // Handle nested category images like "categories.0.images.1.url"
    if (currentImageField.includes('categories.')) {
      const parts = currentImageField.split('.');
      if (parts.length === 5 && parts[0] === 'categories') {
        const categoryIndex = parseInt(parts[1]);
        const imageIndex = parseInt(parts[3]);
        const newCategories = [...blockData.categories];
        newCategories[categoryIndex].images[imageIndex].url = imageUrl;
        handleFieldChange('categories', newCategories);
      }
    } else if (currentImageField.includes('.')) {
      const parts = currentImageField.split('.');
      if (parts.length === 3) {
        const [arrayName, indexStr, fieldName] = parts;
        const index = parseInt(indexStr);
        const newArray = [...(blockData[arrayName] || [])];
        newArray[index] = { ...newArray[index], [fieldName]: imageUrl };
        handleFieldChange(arrayName, newArray);
      } else {
        handleFieldChange(currentImageField, imageUrl);
      }
    } else {
      handleFieldChange(currentImageField, imageUrl);
    }
  }
  setMediaPickerOpen(false);
  setCurrentImageField(null);
};

  const handleImageRemove = (fieldName: string) => {
    handleFieldChange(fieldName, '');
  };

const renderArtistSelector = (field: any, artistIds: string[]) => {

      const handleArtistSearch = async (searchTerm: string) => {
        if (!searchTerm || searchTerm.length < 2) {
          setArtistResults([]);
          return;
        }

        setLoadingArtists(true);
   try {
  const results = await apiClient.get<any[]>(`/api/bandrecommendations/search?q=${encodeURIComponent(searchTerm)}`);
  setArtistResults(results);
} catch (error) {
  console.error('Error searching artists:', error);
} finally {
  setLoadingArtists(false);
}
      };

      const handleAddArtist = (artist: any) => {
        const maxItems = field.maxItems || 5;
        
        if (selectedArtists.length >= maxItems) {
          alert(`Maximum ${maxItems} artists allowed`);
          return;
        }

        if (selectedArtists.find(a => a._id === artist._id)) {
          return;
        }

        const newSelected = [...selectedArtists, artist];
        setSelectedArtists(newSelected);
        handleFieldChange('artistIds', newSelected.map(a => a._id));
        setArtistSearchOpen(false);
        setArtistSearch('');
        setArtistResults([]);
      };

      const handleRemoveArtist = (artistId: string) => {
        const newSelected = selectedArtists.filter(a => a._id !== artistId);
        setSelectedArtists(newSelected);
        handleFieldChange('artistIds', newSelected.map(a => a._id));
      };

      return (
        <Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {selectedArtists.map((artist) => (
              <Chip
                key={artist._id}
                label={artist.artistName}
                onDelete={() => handleRemoveArtist(artist._id)}
                avatar={artist.mainImage ? <Avatar src={artist.mainImage} /> : undefined}
                sx={{
                  bgcolor: '#2C3E50',
                  color: '#ffffff',
                  '& .MuiChip-deleteIcon': { color: '#A4B0BE' }
                }}
              />
            ))}
          </Box>

          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setArtistSearchOpen(true)}
            disabled={selectedArtists.length >= (field.maxItems || 5)}
            sx={{
              borderColor: '#A4B0BE',
              color: '#A4B0BE',
              '&:hover': { borderColor: '#3A9188', color: '#3A9188' }
            }}
          >
            Add Artist ({selectedArtists.length}/{field.maxItems || 5})
          </Button>

          <Dialog 
            open={artistSearchOpen} 
            onClose={() => setArtistSearchOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Search Artists</DialogTitle>
            <DialogContent>
              <TextField
                fullWidth
                placeholder="Search by artist name..."
                value={artistSearch}
                onChange={(e) => {
                  setArtistSearch(e.target.value);
                  handleArtistSearch(e.target.value);
                }}
                sx={{ mb: 2, mt: 1 }}
              />

              {loadingArtists && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              )}

              <List>
                {artistResults.map((artist) => (
                  <ListItem
                    key={artist._id}
                    onClick={() => handleAddArtist(artist)}
                    disabled={selectedArtists.find(a => a._id === artist._id)}
                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'rgba(58, 145, 136, 0.1)' } }}
                  >
                    <ListItemText primary={artist.artistName} />
                  </ListItem>
                ))}
              </List>

              {!loadingArtists && artistSearch && artistResults.length === 0 && (
                <Typography color="text.secondary" align="center">
                  No artists found
                </Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setArtistSearchOpen(false)}>Close</Button>
            </DialogActions>
          </Dialog>
        </Box>
      );
    };

  // Generic renderer for known array fields
const renderArrayField = (field: any) => {
const value = blockData[field.name] || (
  ['ageDistribution', 'professionalProfile', 'geographicReach'].includes(field.name)
    ? { data: [], showPieChart: true }
    : []
);
    const handleAdd = () => {
      let newItem: any = '';
      switch (field.name) {
        case 'metrics':
           newItem = { 
    label: '', 
    value: '', 
    format: 'number',
    type: 'static',
    category: '',
    source: '',
    icon: '',
    context: ''
  };
  break;

  case 'programs':
  newItem = {
    id: `program-${Date.now()}`,
    image: '',
    name: '',
    status: 'active',
    year: new Date().getFullYear(),
    description: '',
    keyDetails: [],
    categories: [],
    link: '',
    linkText: 'Learn More',
    testimonial: {
      enabled: false,
      quote: '',
      partnerName: '',
      partnerRole: '',
      partnerImage: ''
    },
    fundingAvailable: false
  };
  break;
      case 'socialLinks':
      newItem = {
        platform: 'instagram',
        url: '',
        handle: '',
        followerCount: ''
      };
      break;
      
    case 'resourceLinks':
      newItem = {
        title: '',
        description: '',
        url: '',
        icon: 'document',
        ctaText: 'Learn More',
        openInNewTab: true,
        backgroundColor: ''
      };
      break;

      case 'videos':
      newItem = {
        url: '',
        title: '',
        description: '',
        metricCallout: '',
        category: '',
        thumbnail: '',
        ctaText: '',
        ctaLink: ''
      };
      break;

        case 'offerings':
          newItem = { title: '', description: '', highlights: [], pricingModel: 'fixed' };
          break;
        case 'cases':
  newItem = { 
    partnerName: '', 
    partnerType: 'corporate',
    programme: '',
    challenge: '', 
    solution: '', 
    outcomes: [],
    year: new Date().getFullYear()
  };
  break;
      case 'categories':
      newItem = {
        name: 'digital',
        label: 'Digital',
        images: [],
        metricCallout: '',
        description: '',
        anchorLinkText: '',
        anchorLinkBlockId: ''
      };
      break;
   case 'values':
  newItem = '';  // Simple string for each value
  break;
  case 'featuredArtists':
  newItem = '';
  break;
  case 'testimonials':
  newItem = { artist: '', organization: '', quote: '', image: '' };
  break;
    case 'members':
          newItem = { name: '', role: '', bio: '', image: '', credentials: [], linkedin: '' };
          break;
        case 'advisors':
          newItem = { name: '', expertise: '', bio: '', image: '' };
          break;
          case 'images':
  newItem = { url: '', caption: '' };
  break;
        case 'fields':
          newItem = { name: '', type: 'text', label: '', required: false };
          break;

        case 'mediaIds':
          newItem = '';
          break;

            case 'ageDistribution':
      newItem = { ageRange: '', percentage: 0 };
      break;
    
    case 'professionalProfile':
      newItem = { category: '', percentage: 0 };
      break;
    
    case 'geographicReach':
      newItem = { location: '', percentage: 0 };
      break;

          case 'socialMedia':
      newItem = { platform: '', followerCount: 0 };
      break;
    
    case 'pressCoverage':
      newItem = { publication: '', mentionCount: 0 };
      break;

        default:
          newItem = '';
      }
      handleFieldChange(field.name, [...value, newItem]);
    };

    const handleRemove = (index: number) => {
      const newArray = value.filter((_: any, i: number) => i !== index);
      handleFieldChange(field.name, newArray);
    };

    const handleItemChange = (index: number, key: string, val: any) => {
      const newArray = [...value];
      newArray[index] = { ...newArray[index], [key]: val };
      handleFieldChange(field.name, newArray);
    };

const renderMetric = (metric: any, index: number) => (
  <Box
    key={index}
    sx={{
      border: '1px solid #3A9188',
      borderRadius: 2,
      p: 2,
      mb: 2
    }}
  >
    <Typography sx={{ color: '#A4B0BE', mb: 2 }}>Metric {index + 1}</Typography>
    
    {/* Row 1: Label + Value */}
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Label"
          value={metric.label || ''}
          onChange={(e) => handleItemChange(index, 'label', e.target.value)}
          variant="outlined"
          sx={{
            '& .MuiInputLabel-root': { color: '#A4B0BE' },
            '& .MuiOutlinedInput-root': {
              color: '#ffffff',
              '& fieldset': { borderColor: '#A4B0BE' },
              '&:hover fieldset': { borderColor: '#3A9188' },
              '&.Mui-focused fieldset': { borderColor: '#3A9188' }
            }
          }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Value"
          value={metric.value || ''}
          onChange={(e) => handleItemChange(index, 'value', e.target.value)}
          variant="outlined"
          sx={{
            '& .MuiInputLabel-root': { color: '#A4B0BE' },
            '& .MuiOutlinedInput-root': {
              color: '#ffffff',
              '& fieldset': { borderColor: '#A4B0BE' },
              '&:hover fieldset': { borderColor: '#3A9188' },
              '&.Mui-focused fieldset': { borderColor: '#3A9188' }
            }
          }}
        />
      </Grid>
    </Grid>

    {/* Row 2: Format + Type */}
    <Grid container spacing={2} sx={{ mt: 1 }}>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel sx={{ color: '#A4B0BE' }}>Format</InputLabel>
          <Select
            value={metric.format || 'number'}
            onChange={(e) => handleItemChange(index, 'format', e.target.value)}
            sx={{
              color: '#ffffff',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#A4B0BE' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#3A9188' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3A9188' },
              '& .MuiSvgIcon-root': { color: '#A4B0BE' }
            }}
          >
            <MenuItem value="number">Number</MenuItem>
            <MenuItem value="currency">Currency</MenuItem>
            <MenuItem value="percentage">Percentage</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel sx={{ color: '#A4B0BE' }}>Type</InputLabel>
          <Select
            value={metric.type || 'static'}
            onChange={(e) => handleItemChange(index, 'type', e.target.value)}
            sx={{
              color: '#ffffff',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#A4B0BE' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#3A9188' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3A9188' },
              '& .MuiSvgIcon-root': { color: '#A4B0BE' }
            }}
          >
            <MenuItem value="static">Static</MenuItem>
            <MenuItem value="calculated">Calculated</MenuItem>
          </Select>
        </FormControl>
      </Grid>
    </Grid>

    {/* Row 3: Category + Icon */}
    <Grid container spacing={2} sx={{ mt: 1 }}>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel sx={{ color: '#A4B0BE' }}>Category (Optional)</InputLabel>
          <Select
            value={metric.category || ''}
            onChange={(e) => handleItemChange(index, 'category', e.target.value)}
            sx={{
              color: '#ffffff',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#A4B0BE' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#3A9188' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3A9188' },
              '& .MuiSvgIcon-root': { color: '#A4B0BE' }
            }}
          >
            <MenuItem value="">None</MenuItem>
            <MenuItem value="cultural">Cultural</MenuItem>
            <MenuItem value="financial">Financial</MenuItem>
            <MenuItem value="brand">Brand</MenuItem>
            <MenuItem value="audience">Audience</MenuItem>
            <MenuItem value="social">Social</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel sx={{ color: '#A4B0BE' }}>Icon (Optional)</InputLabel>
          <Select
            value={metric.icon || ''}
            onChange={(e) => handleItemChange(index, 'icon', e.target.value)}
            renderValue={(selected) => {
              if (!selected) return <em>None</em>;
              const selectedIcon = METRIC_ICONS.find(icon => icon.value === selected);
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {selectedIcon && (
                    <Box sx={{ display: 'flex', color: '#3A9188' }}>
                      {React.createElement(selectedIcon.icon)}
                    </Box>
                  )}
                  <span>{selectedIcon?.label || selected}</span>
                </Box>
              );
            }}
            sx={{
              color: '#ffffff',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#A4B0BE' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#3A9188' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3A9188' },
              '& .MuiSvgIcon-root': { color: '#A4B0BE' }
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: 400,
                  '& .MuiMenuItem-root': {
                    '&.category-header': {
                      backgroundColor: '#2C3E50',
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      color: '#3A9188',
                      pointerEvents: 'none',
                      paddingTop: 1.5,
                      paddingBottom: 0.5
                    }
                  }
                }
              }
            }}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            
            {Object.entries(CATEGORY_LABELS).map(([categoryKey, categoryLabel]) => {
              const iconsInCategory = getIconsByCategory(categoryKey);
              if (iconsInCategory.length === 0) return null;
              
              return [
                <MenuItem 
                  key={`header-${categoryKey}`} 
                  disabled 
                  className="category-header"
                  sx={{ 
                    backgroundColor: '#2C3E50 !important',
                    opacity: '1 !important'
                  }}
                >
                  {categoryLabel}
                </MenuItem>,
                ...iconsInCategory.map((icon) => (
                  <MenuItem key={icon.value} value={icon.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                      <Box sx={{ display: 'flex', color: '#3A9188', minWidth: 24 }}>
                        {React.createElement(icon.icon)}
                      </Box>
                      <span>{icon.label}</span>
                    </Box>
                  </MenuItem>
                ))
              ];
            })}
          </Select>
        </FormControl>
      </Grid>
    </Grid>

    {/* Row 4: Source */}
    <Box sx={{ mt: 2 }}>
      <TextField
        fullWidth
        label="Source"
        value={metric.source || ''}
        onChange={(e) => handleItemChange(index, 'source', e.target.value)}
        variant="outlined"
        placeholder="e.g., Box Office System, Annual Accounts"
        sx={{
          '& .MuiInputLabel-root': { color: '#A4B0BE' },
          '& .MuiOutlinedInput-root': {
            color: '#ffffff',
            '& fieldset': { borderColor: '#A4B0BE' },
            '&:hover fieldset': { borderColor: '#3A9188' },
            '&.Mui-focused fieldset': { borderColor: '#3A9188' }
          }
        }}
      />
    </Box>

    {/* Row 5: Context */}
    <Box sx={{ mt: 2 }}>
      <TextField
        fullWidth
        multiline
        rows={3}
        label="Context"
        value={metric.context || ''}
        onChange={(e) => handleItemChange(index, 'context', e.target.value)}
        variant="outlined"
        placeholder="Explanatory note or additional details about this metric"
        sx={{
          '& .MuiInputLabel-root': { color: '#A4B0BE' },
          '& .MuiOutlinedInput-root': {
            color: '#ffffff',
            '& fieldset': { borderColor: '#A4B0BE' },
            '&:hover fieldset': { borderColor: '#3A9188' },
            '&.Mui-focused fieldset': { borderColor: '#3A9188' }
          }
        }}
      />
    </Box>
    
    {/* Remove Button */}
    <Button
      variant="outlined"
      color="error"
      startIcon={<DeleteIcon />}
      onClick={() => handleRemove(index)}
      sx={{
        mt: 2,
        borderColor: '#A4B0BE',
        color: '#A4B0BE',
        '&:hover': { borderColor: '#ff1744', color: '#ff1744' }
      }}
    >
      Remove Metric
    </Button>
  </Box>
);

const renderCaseStudy = (caseStudy: any, index: number) => (
  <Box
    key={index}
    sx={{
      border: '1px solid #3A9188',
      borderRadius: 2,
      p: 2,
      mb: 2
    }}
  >
    <Typography sx={{ color: '#A4B0BE', mb: 1 }}>Case Study {index + 1}</Typography>
    
    <TextField
      fullWidth
      label="Partner Name"
      value={caseStudy.partnerName || ''}
      onChange={(e) => handleItemChange(index, 'partnerName', e.target.value)}
      variant="outlined"
      margin="normal"
      sx={{
        '& .MuiInputLabel-root': { color: '#A4B0BE' },
        '& .MuiOutlinedInput-root': {
          color: '#ffffff',
          '& fieldset': { borderColor: '#A4B0BE' },
          '&:hover fieldset': { borderColor: '#3A9188' },
          '&.Mui-focused fieldset': { borderColor: '#3A9188' }
        }
      }}
    />

    <FormControl fullWidth margin="normal">
      <InputLabel sx={{ color: '#A4B0BE' }}>Partner Type</InputLabel>
      <Select
        value={caseStudy.partnerType || 'corporate'}
        onChange={(e) => handleItemChange(index, 'partnerType', e.target.value)}
        sx={{
          color: '#ffffff',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#A4B0BE' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#3A9188' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3A9188' },
          '& .MuiSvgIcon-root': { color: '#A4B0BE' }
        }}
      >
         <MenuItem value="corporate">Corporate Partnership</MenuItem>
    <MenuItem value="foundation">Foundation Grant</MenuItem>
    <MenuItem value="government">Public Funding</MenuItem>
    <MenuItem value="individual">Private Philanthropy</MenuItem>
    <MenuItem value="cultural">Cultural Institution Partnership</MenuItem>
    <MenuItem value="community">Community Collaboration</MenuItem>
    <MenuItem value="educational">Academic / Educational Partnership</MenuItem>
    <MenuItem value="media">Media Partnership</MenuItem>
    <MenuItem value="international">International Collaboration</MenuItem>
    <MenuItem value="venue">Venue / Presentation Partner</MenuItem>
      </Select>
    </FormControl>
    
    <TextField
      fullWidth
      label="Programme"
      value={caseStudy.programme || ''}
      onChange={(e) => handleItemChange(index, 'programme', e.target.value)}
      variant="outlined"
      margin="normal"
      sx={{
        '& .MuiInputLabel-root': { color: '#A4B0BE' },
        '& .MuiOutlinedInput-root': {
          color: '#ffffff',
          '& fieldset': { borderColor: '#A4B0BE' },
          '&:hover fieldset': { borderColor: '#3A9188' },
          '&.Mui-focused fieldset': { borderColor: '#3A9188' }
        }
      }}
    />
    
    <TextField
      fullWidth
      multiline
      rows={3}
      label="Challenge"
      value={caseStudy.challenge || ''}
      onChange={(e) => handleItemChange(index, 'challenge', e.target.value)}
      variant="outlined"
      margin="normal"
      sx={{
        '& .MuiInputLabel-root': { color: '#A4B0BE' },
        '& .MuiOutlinedInput-root': {
          color: '#ffffff',
          '& fieldset': { borderColor: '#A4B0BE' },
          '&:hover fieldset': { borderColor: '#3A9188' },
          '&.Mui-focused fieldset': { borderColor: '#3A9188' }
        }
      }}
    />
    
    <TextField
      fullWidth
      multiline
      rows={3}
      label="Solution"
      value={caseStudy.solution || ''}
      onChange={(e) => handleItemChange(index, 'solution', e.target.value)}
      variant="outlined"
      margin="normal"
      sx={{
        '& .MuiInputLabel-root': { color: '#A4B0BE' },
        '& .MuiOutlinedInput-root': {
          color: '#ffffff',
          '& fieldset': { borderColor: '#A4B0BE' },
          '&:hover fieldset': { borderColor: '#3A9188' },
          '&.Mui-focused fieldset': { borderColor: '#3A9188' }
        }
      }}
    />


<Typography sx={{ color: '#A4B0BE', mt: 2, mb: 1 }}>Outcomes</Typography>
{(caseStudy.outcomes || []).map((outcome: string, outcomeIdx: number) => (
  <Box key={outcomeIdx} sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
    <TextField
      fullWidth
      value={outcome}
      onChange={(e) => {
        const newOutcomes = [...(caseStudy.outcomes || [])];
        newOutcomes[outcomeIdx] = e.target.value;
        handleItemChange(index, 'outcomes', newOutcomes);
      }}
      variant="outlined"
      placeholder={`Outcome ${outcomeIdx + 1}`}
      sx={{
        '& .MuiInputLabel-root': { color: '#A4B0BE' },
        '& .MuiOutlinedInput-root': {
          color: '#ffffff',
          '& fieldset': { borderColor: '#A4B0BE' },
          '&:hover fieldset': { borderColor: '#3A9188' },
          '&.Mui-focused fieldset': { borderColor: '#3A9188' }
        }
      }}
    />
    <IconButton 
      onClick={() => {
        const newOutcomes = (caseStudy.outcomes || []).filter((_: any, i: number) => i !== outcomeIdx);
        handleItemChange(index, 'outcomes', newOutcomes);
      }} 
      sx={{ color: '#A4B0BE' }}
    >
      <DeleteIcon />
    </IconButton>
  </Box>
))}
<Button
  variant="outlined"
  startIcon={<AddIcon />}
  onClick={() => {
    const newOutcomes = [...(caseStudy.outcomes || []), ''];
    handleItemChange(index, 'outcomes', newOutcomes);
  }}
  sx={{
    mt: 1,
    borderColor: '#3A9188',
    color: '#3A9188',
    '&:hover': { borderColor: '#2ECC71', color: '#2ECC71' }
  }}
>
  Add Outcome
</Button>

    <TextField
      fullWidth
      label="Year"
      type="number"
      value={caseStudy.year || new Date().getFullYear()}
      onChange={(e) => handleItemChange(index, 'year', parseInt(e.target.value))}
      variant="outlined"
      margin="normal"
      sx={{
        '& .MuiInputLabel-root': { color: '#A4B0BE' },
        '& .MuiOutlinedInput-root': {
          color: '#ffffff',
          '& fieldset': { borderColor: '#A4B0BE' },
          '&:hover fieldset': { borderColor: '#3A9188' },
          '&.Mui-focused fieldset': { borderColor: '#3A9188' }
        }
      }}
    />
    
    <Button
      variant="outlined"
      color="error"
      startIcon={<DeleteIcon />}
      onClick={() => handleRemove(index)}
      sx={{
        mt: 1,
        borderColor: '#A4B0BE',
        color: '#A4B0BE',
        '&:hover': { borderColor: '#ff1744', color: '#ff1744' }
      }}
    >
      Remove Case Study
    </Button>
  </Box>
);

const renderTestimonial = (testimonial: any, index: number) => (
  <Box
    key={index}
    sx={{
      border: '1px solid #3A9188',
      borderRadius: 2,
      p: 2,
      mb: 2
    }}
  >
    <Typography sx={{ color: '#A4B0BE', mb: 1 }}>Testimonial {index + 1}</Typography>
    
    <TextField
      fullWidth
      label="Artist/Speaker Name"
      value={testimonial.artist || ''}
      onChange={(e) => handleItemChange(index, 'artist', e.target.value)}
      variant="outlined"
      margin="normal"
      sx={{
        '& .MuiInputLabel-root': { color: '#A4B0BE' },
        '& .MuiOutlinedInput-root': {
          color: '#ffffff',
          '& fieldset': { borderColor: '#A4B0BE' },
          '&:hover fieldset': { borderColor: '#3A9188' },
          '&.Mui-focused fieldset': { borderColor: '#3A9188' }
        }
      }}
    />

    <TextField
      fullWidth
      label="Organization (Optional)"
      value={testimonial.organization || ''}
      onChange={(e) => handleItemChange(index, 'organization', e.target.value)}
      variant="outlined"
      margin="normal"
      sx={{
        '& .MuiInputLabel-root': { color: '#A4B0BE' },
        '& .MuiOutlinedInput-root': {
          color: '#ffffff',
          '& fieldset': { borderColor: '#A4B0BE' },
          '&:hover fieldset': { borderColor: '#3A9188' },
          '&.Mui-focused fieldset': { borderColor: '#3A9188' }
        }
      }}
    />
    
    <TextField
      fullWidth
      multiline
      rows={4}
      label="Quote"
      value={testimonial.quote || ''}
      onChange={(e) => handleItemChange(index, 'quote', e.target.value)}
      variant="outlined"
      margin="normal"
      sx={{
        '& .MuiInputLabel-root': { color: '#A4B0BE' },
        '& .MuiOutlinedInput-root': {
          color: '#ffffff',
          '& fieldset': { borderColor: '#A4B0BE' },
          '&:hover fieldset': { borderColor: '#3A9188' },
          '&.Mui-focused fieldset': { borderColor: '#3A9188' }
        }
      }}
    />

    {/* IMAGE PICKER */}
    <Box sx={{ my: 2 }}>
      <Typography variant="body2" gutterBottom sx={{ color: '#ffffff' }}>
        Photo (Optional)
      </Typography>
      {testimonial.image ? (
        <Card sx={{ mb: 1, position: 'relative' }}>
          <CardMedia
            component="img"
            height="200"
            image={testimonial.image}
            alt="Testimonial"
            sx={{ objectFit: 'cover' }}
          />
          <IconButton
            size="small"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'background.paper',
              '&:hover': { bgcolor: 'error.light', color: 'white' }
            }}
            onClick={() => handleItemChange(index, 'image', '')}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Card>
      ) : null}
      <Button
        variant="outlined"
        startIcon={<ImageIcon />}
        onClick={() => {
          // Store which testimonial and field we're editing
          setCurrentImageField(`testimonials.${index}.image`);
          setMediaPickerOpen(true);
        }}
        fullWidth
        sx={{
          justifyContent: 'flex-start',
          py: 1.5,
          color: '#A4B0BE',
          borderColor: '#A4B0BE',
          '&:hover': { borderColor: '#3A9188', color: '#3A9188' }
        }}
      >
        {testimonial.image ? 'Change Photo' : 'Choose Photo'}
      </Button>
    </Box>
    
    <Button
      variant="outlined"
      color="error"
      startIcon={<DeleteIcon />}
      onClick={() => handleRemove(index)}
      sx={{
        mt: 1,
        borderColor: '#A4B0BE',
        color: '#A4B0BE',
        '&:hover': { borderColor: '#ff1744', color: '#ff1744' }
      }}
    >
      Remove Testimonial
    </Button>
  </Box>
);

const renderPastImpactMetric = (metric: any, index: number) => (
  <Box
    key={index}
    sx={{
      border: '1px solid #3A9188',
      borderRadius: 2,
      p: 2,
      mb: 2
    }}
  >
    <Typography sx={{ color: '#A4B0BE', mb: 1 }}>Metric {index + 1}</Typography>
    
    <TextField
      fullWidth
      label="Label (e.g., 'Performances Delivered')"
      value={metric.label || ''}
      onChange={(e) => handleItemChange(index, 'label', e.target.value)}
      variant="outlined"
      margin="normal"
      sx={{
        '& .MuiInputLabel-root': { color: '#A4B0BE' },
        '& .MuiOutlinedInput-root': {
          color: '#ffffff',
          '& fieldset': { borderColor: '#A4B0BE' },
          '&:hover fieldset': { borderColor: '#3A9188' },
          '&.Mui-focused fieldset': { borderColor: '#3A9188' }
        }
      }}
    />
    
    <TextField
      fullWidth
      label="Value (number)"
      type="number"
      value={metric.value || 0}
      onChange={(e) => handleItemChange(index, 'value', parseInt(e.target.value) || 0)}
      variant="outlined"
      margin="normal"
      sx={{
        '& .MuiInputLabel-root': { color: '#A4B0BE' },
        '& .MuiOutlinedInput-root': {
          color: '#ffffff',
          '& fieldset': { borderColor: '#A4B0BE' },
          '&:hover fieldset': { borderColor: '#3A9188' },
          '&.Mui-focused fieldset': { borderColor: '#3A9188' }
        }
      }}
    />
    
    <Button
      variant="outlined"
      color="error"
      startIcon={<DeleteIcon />}
      onClick={() => handleRemove(index)}
      sx={{
        mt: 1,
        borderColor: '#A4B0BE',
        color: '#A4B0BE',
        '&:hover': { borderColor: '#ff1744', color: '#ff1744' }
      }}
    >
      Remove Metric
    </Button>
  </Box>
);

const renderFeaturedArtist = (artist: string, index: number) => (
  <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
    <TextField
      fullWidth
      value={artist}
      onChange={(e) => {
        const newArr = [...(blockData.featuredArtists || [])];
        newArr[index] = e.target.value;
        handleFieldChange('featuredArtists', newArr);
      }}
      variant="outlined"
      placeholder="Artist Name (Country)"
      sx={{
        '& .MuiOutlinedInput-root': {
          color: '#ffffff',
          '& fieldset': { borderColor: '#A4B0BE' },
          '&:hover fieldset': { borderColor: '#3A9188' },
          '&.Mui-focused fieldset': { borderColor: '#3A9188' }
        }
      }}
    />
    <IconButton 
      onClick={() => {
        const newArr = (blockData.featuredArtists || []).filter(
          (_: string, i: number) => i !== index
        );
        handleFieldChange('featuredArtists', newArr);
      }} 
      sx={{ ml: 1, color: '#A4B0BE' }}
    >
      <DeleteIcon />
    </IconButton>
  </Box>
);

const renderProgram = (program: any, index: number) => (
  <Box
    key={index}
    sx={{
      border: '1px solid #3A9188',
      borderRadius: 2,
      p: 2,
      mb: 2
    }}
  >
    <Typography sx={{ color: '#A4B0BE', mb: 2, fontWeight: 600 }}>
      Program {index + 1}
    </Typography>

    {/* Image */}
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" sx={{ color: '#ffffff', mb: 1 }}>
        Program Image
      </Typography>
      {program.image && (
        <Card sx={{ mb: 1, position: 'relative' }}>
          <CardMedia
            component="img"
            height="150"
            image={program.image}
            sx={{ objectFit: 'cover' }}
          />
          <IconButton
            size="small"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'background.paper',
              '&:hover': { bgcolor: 'error.light', color: 'white' }
            }}
            onClick={() => handleItemChange(index, 'image', '')}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Card>
      )}
      <Button
        variant="outlined"
        startIcon={<ImageIcon />}
        onClick={() => handleImagePickerOpen(`programs.${index}.image`)}
        fullWidth
        sx={{
          borderColor: '#A4B0BE',
          color: '#A4B0BE',
          '&:hover': { borderColor: '#3A9188', color: '#3A9188' }
        }}
      >
        {program.image ? 'Change Image' : 'Choose Image'}
      </Button>
    </Box>

    {/* Name */}
    <TextField
      fullWidth
      label="Program Name"
      value={program.name || ''}
      onChange={(e) => handleItemChange(index, 'name', e.target.value)}
      margin="normal"
      sx={{
        '& .MuiInputLabel-root': { color: '#A4B0BE' },
        '& .MuiOutlinedInput-root': {
          color: '#ffffff',
          '& fieldset': { borderColor: '#A4B0BE' },
          '&:hover fieldset': { borderColor: '#3A9188' },
          '&.Mui-focused fieldset': { borderColor: '#3A9188' }
        }
      }}
    />

    {/* Status */}
    <FormControl fullWidth margin="normal">
      <InputLabel sx={{ color: '#A4B0BE' }}>Status</InputLabel>
      <Select
        value={program.status || 'active'}
        onChange={(e) => handleItemChange(index, 'status', e.target.value)}
        label="Status"
        sx={{
          color: '#ffffff',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#A4B0BE' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#3A9188' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3A9188' },
          '& .MuiSvgIcon-root': { color: '#A4B0BE' }
        }}
      >
        <MenuItem value="active">Active</MenuItem>
        <MenuItem value="past">Past</MenuItem>
        <MenuItem value="seasonal">Seasonal</MenuItem>
        <MenuItem value="on-demand">On-Demand</MenuItem>
      </Select>
    </FormControl>

    {/* Year */}
    <TextField
      fullWidth
      type="number"
      label="Year"
      value={program.year || new Date().getFullYear()}
      onChange={(e) => handleItemChange(index, 'year', parseInt(e.target.value))}
      margin="normal"
      sx={{
        '& .MuiInputLabel-root': { color: '#A4B0BE' },
        '& .MuiOutlinedInput-root': {
          color: '#ffffff',
          '& fieldset': { borderColor: '#A4B0BE' },
          '&:hover fieldset': { borderColor: '#3A9188' },
          '&.Mui-focused fieldset': { borderColor: '#3A9188' }
        }
      }}
    />

    {/* Description */}
    <TextField
      fullWidth
      multiline
      rows={2}
      label="One-line Description"
      value={program.description || ''}
      onChange={(e) => handleItemChange(index, 'description', e.target.value)}
      margin="normal"
      sx={{
        '& .MuiInputLabel-root': { color: '#A4B0BE' },
        '& .MuiOutlinedInput-root': {
          color: '#ffffff',
          '& fieldset': { borderColor: '#A4B0BE' },
          '&:hover fieldset': { borderColor: '#3A9188' },
          '&.Mui-focused fieldset': { borderColor: '#3A9188' }
        }
      }}
    />

    {/* Key Details Array */}
    <Box sx={{ mt: 2 }}>
      <Typography variant="body2" sx={{ color: '#ffffff', mb: 1 }}>
        Key Details
      </Typography>
      {(program.keyDetails || []).map((detail: string, detailIdx: number) => (
        <Box key={detailIdx} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <TextField
            fullWidth
            value={detail}
            onChange={(e) => {
              const newDetails = [...(program.keyDetails || [])];
              newDetails[detailIdx] = e.target.value;
              handleItemChange(index, 'keyDetails', newDetails);
            }}
            placeholder="e.g., 40+ artists annually"
            sx={{
              '& .MuiOutlinedInput-root': {
                color: '#ffffff',
                '& fieldset': { borderColor: '#A4B0BE' },
                '&:hover fieldset': { borderColor: '#3A9188' },
                '&.Mui-focused fieldset': { borderColor: '#3A9188' }
              }
            }}
          />
          <IconButton
            onClick={() => {
              const newDetails = (program.keyDetails || []).filter((_: any, i: number) => i !== detailIdx);
              handleItemChange(index, 'keyDetails', newDetails);
            }}
            sx={{ ml: 1, color: '#A4B0BE' }}
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      ))}
      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={() => {
          const newDetails = [...(program.keyDetails || []), ''];
          handleItemChange(index, 'keyDetails', newDetails);
        }}
        sx={{
          mt: 1,
          borderColor: '#A4B0BE',
          color: '#A4B0BE',
          '&:hover': { borderColor: '#3A9188', color: '#3A9188' }
        }}
      >
        Add Detail
      </Button>
    </Box>

    {/* Categories Multi-Select */}
    <FormControl fullWidth margin="normal">
      <InputLabel sx={{ color: '#A4B0BE' }}>Categories</InputLabel>
      <Select
        multiple
        value={program.categories || []}
        onChange={(e) => handleItemChange(index, 'categories', e.target.value)}
        label="Categories"
        sx={{
          color: '#ffffff',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#A4B0BE' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#3A9188' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3A9188' },
          '& .MuiSvgIcon-root': { color: '#A4B0BE' }
        }}
        renderValue={(selected) => (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {(selected as string[]).map((value) => (
              <Chip key={value} label={value} size="small" />
            ))}
          </Box>
        )}
      >
        <MenuItem value="Artist Development">Artist Development</MenuItem>
        <MenuItem value="Commissioning">Commissioning</MenuItem>
        <MenuItem value="Programming">Programming</MenuItem>
        <MenuItem value="Festivals">Festivals</MenuItem>
        <MenuItem value="International">International</MenuItem>
        <MenuItem value="Export">Export</MenuItem>
        <MenuItem value="Community & Education">Community & Education</MenuItem>
        <MenuItem value="Distribution & Media">Distribution & Media</MenuItem>
        <MenuItem value="Production Services">Production Services</MenuItem>
        <MenuItem value="National Network">National Network</MenuItem>
        <MenuItem value="Policy Initiatives">Policy Initiatives</MenuItem>
        <MenuItem value="Accessibility">Accessibility</MenuItem>
        <MenuItem value="Sustainability">Sustainability</MenuItem>
        <MenuItem value="Past Initiative">Past Initiative</MenuItem>
      </Select>
    </FormControl>

    {/* Link */}
    <TextField
      fullWidth
      label="Link URL"
      value={program.link || ''}
      onChange={(e) => handleItemChange(index, 'link', e.target.value)}
      margin="normal"
      placeholder="https://..."
      sx={{
        '& .MuiInputLabel-root': { color: '#A4B0BE' },
        '& .MuiOutlinedInput-root': {
          color: '#ffffff',
          '& fieldset': { borderColor: '#A4B0BE' },
          '&:hover fieldset': { borderColor: '#3A9188' },
          '&.Mui-focused fieldset': { borderColor: '#3A9188' }
        }
      }}
    />

    <TextField
      fullWidth
      label="Link Text"
      value={program.linkText || ''}
      onChange={(e) => handleItemChange(index, 'linkText', e.target.value)}
      margin="normal"
      placeholder="Learn More"
      sx={{
        '& .MuiInputLabel-root': { color: '#A4B0BE' },
        '& .MuiOutlinedInput-root': {
          color: '#ffffff',
          '& fieldset': { borderColor: '#A4B0BE' },
          '&:hover fieldset': { borderColor: '#3A9188' },
          '&.Mui-focused fieldset': { borderColor: '#3A9188' }
        }
      }}
    />

    {/* Funding Available Toggle */}
    <FormControlLabel
      control={
        <Switch
          checked={program.fundingAvailable || false}
          onChange={(e) => handleItemChange(index, 'fundingAvailable', e.target.checked)}
        />
      }
      label="Funding Available"
      sx={{ mt: 2, color: '#ffffff' }}
    />

    {/* Testimonial Section */}
    <Box sx={{ mt: 3, p: 2, border: '1px solid #3A9188', borderRadius: 1 }}>
      <FormControlLabel
        control={
          <Switch
            checked={program.testimonial?.enabled || false}
            onChange={(e) => {
              const newTestimonial = { ...(program.testimonial || {}), enabled: e.target.checked };
              handleItemChange(index, 'testimonial', newTestimonial);
            }}
          />
        }
        label="Include Partner Testimonial"
        sx={{ color: '#ffffff', mb: 2 }}
      />

      {program.testimonial?.enabled && (
        <>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Quote"
            value={program.testimonial.quote || ''}
            onChange={(e) => {
              const newTestimonial = { ...program.testimonial, quote: e.target.value };
              handleItemChange(index, 'testimonial', newTestimonial);
            }}
            margin="normal"
            sx={{
              '& .MuiInputLabel-root': { color: '#A4B0BE' },
              '& .MuiOutlinedInput-root': {
                color: '#ffffff',
                '& fieldset': { borderColor: '#A4B0BE' },
                '&:hover fieldset': { borderColor: '#3A9188' },
                '&.Mui-focused fieldset': { borderColor: '#3A9188' }
              }
            }}
          />

          <TextField
            fullWidth
            label="Partner Name"
            value={program.testimonial.partnerName || ''}
            onChange={(e) => {
              const newTestimonial = { ...program.testimonial, partnerName: e.target.value };
              handleItemChange(index, 'testimonial', newTestimonial);
            }}
            margin="normal"
            sx={{
              '& .MuiInputLabel-root': { color: '#A4B0BE' },
              '& .MuiOutlinedInput-root': {
                color: '#ffffff',
                '& fieldset': { borderColor: '#A4B0BE' },
                '&:hover fieldset': { borderColor: '#3A9188' },
                '&.Mui-focused fieldset': { borderColor: '#3A9188' }
              }
            }}
          />

          <TextField
            fullWidth
            label="Partner Role/Organization"
            value={program.testimonial.partnerRole || ''}
            onChange={(e) => {
              const newTestimonial = { ...program.testimonial, partnerRole: e.target.value };
              handleItemChange(index, 'testimonial', newTestimonial);
            }}
            margin="normal"
            placeholder="e.g., Director, Arts Council"
            sx={{
              '& .MuiInputLabel-root': { color: '#A4B0BE' },
              '& .MuiOutlinedInput-root': {
                color: '#ffffff',
                '& fieldset': { borderColor: '#A4B0BE' },
                '&:hover fieldset': { borderColor: '#3A9188' },
                '&.Mui-focused fieldset': { borderColor: '#3A9188' }
              }
            }}
          />

          {/* Partner Image */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ color: '#ffffff', mb: 1 }}>
              Partner Image (Optional)
            </Typography>
            {program.testimonial.partnerImage && (
              <Card sx={{ mb: 1, position: 'relative' }}>
                <CardMedia
                  component="img"
                  height="100"
                  image={program.testimonial.partnerImage}
                  sx={{ objectFit: 'cover' }}
                />
                <IconButton
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    bgcolor: 'background.paper',
                    '&:hover': { bgcolor: 'error.light', color: 'white' }
                  }}
                  onClick={() => {
                    const newTestimonial = { ...program.testimonial, partnerImage: '' };
                    handleItemChange(index, 'testimonial', newTestimonial);
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Card>
            )}
            <Button
              variant="outlined"
              startIcon={<ImageIcon />}
              onClick={() => handleImagePickerOpen(`programs.${index}.testimonial.partnerImage`)}
              fullWidth
              sx={{
                borderColor: '#A4B0BE',
                color: '#A4B0BE',
                '&:hover': { borderColor: '#3A9188', color: '#3A9188' }
              }}
            >
              {program.testimonial.partnerImage ? 'Change Image' : 'Choose Image'}
            </Button>
          </Box>
        </>
      )}
    </Box>

    {/* Remove Program Button */}
    <Button
      variant="outlined"
      color="error"
      startIcon={<DeleteIcon />}
      onClick={() => handleRemove(index)}
      fullWidth
      sx={{
        mt: 2,
        borderColor: '#A4B0BE',
        color: '#A4B0BE',
        '&:hover': { borderColor: '#ff1744', color: '#ff1744' }
      }}
    >
      Remove Program
    </Button>
  </Box>
);


const renderAgeDistribution = (item: any, index: number) => (
  <Box
    key={index}
    sx={{
      border: '1px solid #3A9188',
      borderRadius: 2,
      p: 2,
      mb: 2
    }}
  >
    <Typography sx={{ color: '#A4B0BE', mb: 1 }}>Age Range {index + 1}</Typography>
    
    <TextField
      fullWidth
      label="Age Range (e.g., 25-34)"
      value={item.ageRange || ''}
      onChange={(e) => handleItemChange(index, 'ageRange', e.target.value)}
      variant="outlined"
      sx={{
        mb: 2,
        '& .MuiInputLabel-root': { color: '#A4B0BE' },
        '& .MuiOutlinedInput-root': {
          color: '#ffffff',
          '& fieldset': { borderColor: '#A4B0BE' },
          '&:hover fieldset': { borderColor: '#3A9188' },
          '&.Mui-focused fieldset': { borderColor: '#3A9188' }
        }
      }}
    />
    
    <TextField
      fullWidth
      type="number"
      label="Percentage"
      value={item.percentage || 0}
      onChange={(e) => handleItemChange(index, 'percentage', parseFloat(e.target.value))}
      variant="outlined"
      inputProps={{ min: 0, max: 100, step: 1 }}
      sx={{
        '& .MuiInputLabel-root': { color: '#A4B0BE' },
        '& .MuiOutlinedInput-root': {
          color: '#ffffff',
          '& fieldset': { borderColor: '#A4B0BE' },
          '&:hover fieldset': { borderColor: '#3A9188' },
          '&.Mui-focused fieldset': { borderColor: '#3A9188' }
        }
      }}
    />
    
    <Button
      variant="outlined"
      color="error"
      startIcon={<DeleteIcon />}
      onClick={() => handleRemove(index)}
      sx={{
        mt: 2,
        borderColor: '#A4B0BE',
        color: '#A4B0BE',
        '&:hover': { borderColor: '#ff1744', color: '#ff1744' }
      }}
    >
      Remove
    </Button>
  </Box>
);

const renderProfessionalProfile = (item: any, index: number) => {
  const categories = [
    'Tech & Creative Industries',
    'Finance & Legal',
    'Education & Arts',
    'Healthcare & Medical',
    'Engineering & Manufacturing',
    'Hospitality & Tourism',
    'Media & Communications',
    'Government & Public Sector',
    'Retail & Consumer Services',
    'Other Professionals'
  ];

  return (
    <Box
      key={index}
      sx={{
        border: '1px solid #3A9188',
        borderRadius: 2,
        p: 2,
        mb: 2
      }}
    >
      <Typography sx={{ color: '#A4B0BE', mb: 1 }}>Professional Category {index + 1}</Typography>
      
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel sx={{ color: '#A4B0BE' }}>Category</InputLabel>
        <Select
          value={item.category || ''}
          onChange={(e) => handleItemChange(index, 'category', e.target.value)}
          sx={{
            color: '#ffffff',
            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#A4B0BE' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#3A9188' },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3A9188' }
          }}
        >
          {categories.map((cat) => (
            <MenuItem key={cat} value={cat}>{cat}</MenuItem>
          ))}
        </Select>
      </FormControl>
      
      <TextField
        fullWidth
        type="number"
        label="Percentage"
        value={item.percentage || 0}
        onChange={(e) => handleItemChange(index, 'percentage', parseFloat(e.target.value))}
        variant="outlined"
        inputProps={{ min: 0, max: 100, step: 1 }}
        sx={{
          '& .MuiInputLabel-root': { color: '#A4B0BE' },
          '& .MuiOutlinedInput-root': {
            color: '#ffffff',
            '& fieldset': { borderColor: '#A4B0BE' },
            '&:hover fieldset': { borderColor: '#3A9188' },
            '&.Mui-focused fieldset': { borderColor: '#3A9188' }
          }
        }}
      />
      
      <Button
        variant="outlined"
        color="error"
        startIcon={<DeleteIcon />}
        onClick={() => handleRemove(index)}
        sx={{
          mt: 2,
          borderColor: '#A4B0BE',
          color: '#A4B0BE',
          '&:hover': { borderColor: '#ff1744', color: '#ff1744' }
        }}
      >
        Remove
      </Button>
    </Box>
  );
};

const renderGeographicReach = (item: any, index: number) => (
  <Box
    key={index}
    sx={{
      border: '1px solid #3A9188',
      borderRadius: 2,
      p: 2,
      mb: 2
    }}
  >
    <Typography sx={{ color: '#A4B0BE', mb: 1 }}>Location {index + 1}</Typography>
    
    <TextField
      fullWidth
      label="Location (e.g., Dublin 4/6/8)"
      value={item.location || ''}
      onChange={(e) => handleItemChange(index, 'location', e.target.value)}
      variant="outlined"
      sx={{
        mb: 2,
        '& .MuiInputLabel-root': { color: '#A4B0BE' },
        '& .MuiOutlinedInput-root': {
          color: '#ffffff',
          '& fieldset': { borderColor: '#A4B0BE' },
          '&:hover fieldset': { borderColor: '#3A9188' },
          '&.Mui-focused fieldset': { borderColor: '#3A9188' }
        }
      }}
    />
    
    <TextField
      fullWidth
      type="number"
      label="Percentage"
      value={item.percentage || 0}
      onChange={(e) => handleItemChange(index, 'percentage', parseFloat(e.target.value))}
      variant="outlined"
      inputProps={{ min: 0, max: 100, step: 1 }}
      sx={{
        '& .MuiInputLabel-root': { color: '#A4B0BE' },
        '& .MuiOutlinedInput-root': {
          color: '#ffffff',
          '& fieldset': { borderColor: '#A4B0BE' },
          '&:hover fieldset': { borderColor: '#3A9188' },
          '&.Mui-focused fieldset': { borderColor: '#3A9188' }
        }
      }}
    />
    
    <Button
      variant="outlined"
      color="error"
      startIcon={<DeleteIcon />}
      onClick={() => handleRemove(index)}
      sx={{
        mt: 2,
        borderColor: '#A4B0BE',
        color: '#A4B0BE',
        '&:hover': { borderColor: '#ff1744', color: '#ff1744' }
      }}
    >
      Remove
    </Button>
  </Box>
);

const renderSocialMedia = (item: any, index: number) => (
  <Box
    key={index}
    sx={{
      border: '1px solid #3A9188',
      borderRadius: 2,
      p: 2,
      mb: 2
    }}
  >
    <Typography sx={{ color: '#A4B0BE', mb: 1 }}>Platform {index + 1}</Typography>
    
    <TextField
      fullWidth
      label="Platform Name (e.g., Instagram)"
      value={item.platform || ''}
      onChange={(e) => handleItemChange(index, 'platform', e.target.value)}
      variant="outlined"
      sx={{
        mb: 2,
        '& .MuiInputLabel-root': { color: '#A4B0BE' },
        '& .MuiOutlinedInput-root': {
          color: '#ffffff',
          '& fieldset': { borderColor: '#A4B0BE' },
          '&:hover fieldset': { borderColor: '#3A9188' },
          '&.Mui-focused fieldset': { borderColor: '#3A9188' }
        }
      }}
    />
    
    <TextField
      fullWidth
      type="number"
      label="Follower Count"
      value={item.followerCount || 0}
      onChange={(e) => handleItemChange(index, 'followerCount', parseInt(e.target.value))}
      variant="outlined"
      inputProps={{ min: 0, step: 1 }}
      sx={{
        '& .MuiInputLabel-root': { color: '#A4B0BE' },
        '& .MuiOutlinedInput-root': {
          color: '#ffffff',
          '& fieldset': { borderColor: '#A4B0BE' },
          '&:hover fieldset': { borderColor: '#3A9188' },
          '&.Mui-focused fieldset': { borderColor: '#3A9188' }
        }
      }}
    />
    
    <Button
      variant="outlined"
      color="error"
      startIcon={<DeleteIcon />}
      onClick={() => handleRemove(index)}
      sx={{
        mt: 2,
        borderColor: '#A4B0BE',
        color: '#A4B0BE',
        '&:hover': { borderColor: '#ff1744', color: '#ff1744' }
      }}
    >
      Remove
    </Button>
  </Box>
);

const renderPressCoverage = (item: any, index: number) => (
  <Box
    key={index}
    sx={{
      border: '1px solid #3A9188',
      borderRadius: 2,
      p: 2,
      mb: 2
    }}
  >
    <Typography sx={{ color: '#A4B0BE', mb: 1 }}>Publication {index + 1}</Typography>
    
    <TextField
      fullWidth
      label="Publication Name (e.g., Irish Times)"
      value={item.publication || ''}
      onChange={(e) => handleItemChange(index, 'publication', e.target.value)}
      variant="outlined"
      sx={{
        mb: 2,
        '& .MuiInputLabel-root': { color: '#A4B0BE' },
        '& .MuiOutlinedInput-root': {
          color: '#ffffff',
          '& fieldset': { borderColor: '#A4B0BE' },
          '&:hover fieldset': { borderColor: '#3A9188' },
          '&.Mui-focused fieldset': { borderColor: '#3A9188' }
        }
      }}
    />
    
    <TextField
      fullWidth
      type="number"
      label="Mention Count"
      value={item.mentionCount || 0}
      onChange={(e) => handleItemChange(index, 'mentionCount', parseInt(e.target.value))}
      variant="outlined"
      inputProps={{ min: 0, step: 1 }}
      sx={{
        '& .MuiInputLabel-root': { color: '#A4B0BE' },
        '& .MuiOutlinedInput-root': {
          color: '#ffffff',
          '& fieldset': { borderColor: '#A4B0BE' },
          '&:hover fieldset': { borderColor: '#3A9188' },
          '&.Mui-focused fieldset': { borderColor: '#3A9188' }
        }
      }}
    />
    
    <Button
      variant="outlined"
      color="error"
      startIcon={<DeleteIcon />}
      onClick={() => handleRemove(index)}
      sx={{
        mt: 2,
        borderColor: '#A4B0BE',
        color: '#A4B0BE',
        '&:hover': { borderColor: '#ff1744', color: '#ff1744' }
      }}
    >
      Remove
    </Button>
  </Box>
);

const renderCategory = (category: any, categoryIndex: number) => {
  const handleCategoryChange = (field: string, val: any) => {
    const newCategories = [...value];
    newCategories[categoryIndex] = { ...newCategories[categoryIndex], [field]: val };
    handleFieldChange('categories', newCategories);
  };

  const handleImageAdd = () => {
    const newCategories = [...value];
    newCategories[categoryIndex].images = [
      ...(newCategories[categoryIndex].images || []),
      { url: '', caption: '' }
    ];
    handleFieldChange('categories', newCategories);
  };

  const handleImageRemove = (imageIndex: number) => {
    const newCategories = [...value];
    newCategories[categoryIndex].images = newCategories[categoryIndex].images.filter(
      (_: any, i: number) => i !== imageIndex
    );
    handleFieldChange('categories', newCategories);
  };

  const handleImageChange = (imageIndex: number, field: string, val: any) => {
    const newCategories = [...value];
    newCategories[categoryIndex].images[imageIndex] = {
      ...newCategories[categoryIndex].images[imageIndex],
      [field]: val
    };
    handleFieldChange('categories', newCategories);
  };

  return (
    <Box
      key={categoryIndex}
      sx={{
        border: '2px solid #3A9188',
        borderRadius: 2,
        p: 3,
        mb: 3,
        bgcolor: '#2C3E50'
      }}
    >
      <Typography variant="h6" sx={{ color: '#ffffff', mb: 2 }}>
        Category {categoryIndex + 1}
      </Typography>

      {/* Category Name */}
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel sx={{ color: '#A4B0BE' }}>Category Type</InputLabel>
        <Select
          value={category.name || 'digital'}
          onChange={(e) => handleCategoryChange('name', e.target.value)}
          sx={{
            color: '#ffffff',
            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#A4B0BE' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#3A9188' },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3A9188' }
          }}
        >
          <MenuItem value="digital">Digital</MenuItem>
          <MenuItem value="print">Print</MenuItem>
          <MenuItem value="venue">Venue</MenuItem>
          <MenuItem value="press">Press</MenuItem>
        </Select>
      </FormControl>

      {/* Category Label */}
      <TextField
        fullWidth
        label="Display Label"
        value={category.label || ''}
        onChange={(e) => handleCategoryChange('label', e.target.value)}
        variant="outlined"
        sx={{
          mb: 2,
          '& .MuiInputLabel-root': { color: '#A4B0BE' },
          '& .MuiOutlinedInput-root': {
            color: '#ffffff',
            '& fieldset': { borderColor: '#A4B0BE' },
            '&:hover fieldset': { borderColor: '#3A9188' },
            '&.Mui-focused fieldset': { borderColor: '#3A9188' }
          }
        }}
      />

      {/* Metric Callout */}
      <TextField
        fullWidth
        label="Metric Callout"
        value={category.metricCallout || ''}
        onChange={(e) => handleCategoryChange('metricCallout', e.target.value)}
        variant="outlined"
        placeholder="e.g., 18K monthly website visitors"
        sx={{
          mb: 2,
          '& .MuiInputLabel-root': { color: '#A4B0BE' },
          '& .MuiOutlinedInput-root': {
            color: '#ffffff',
            '& fieldset': { borderColor: '#A4B0BE' },
            '&:hover fieldset': { borderColor: '#3A9188' },
            '&.Mui-focused fieldset': { borderColor: '#3A9188' }
          }
        }}
      />

      {/* Description */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ color: '#A4B0BE', mb: 1 }}>
          Description (Rich Text)
        </Typography>
        <RichTextEditor
          value={category.description || ''}
          onChange={(html) => handleCategoryChange('description', html)}
        />
      </Box>

      {/* Anchor Link */}
      <TextField
        fullWidth
        label="Anchor Link Text (Optional)"
        value={category.anchorLinkText || ''}
        onChange={(e) => handleCategoryChange('anchorLinkText', e.target.value)}
        variant="outlined"
        placeholder="e.g., See our full media reach"
        sx={{
          mb: 2,
          '& .MuiInputLabel-root': { color: '#A4B0BE' },
          '& .MuiOutlinedInput-root': {
            color: '#ffffff',
            '& fieldset': { borderColor: '#A4B0BE' },
            '&:hover fieldset': { borderColor: '#3A9188' },
            '&.Mui-focused fieldset': { borderColor: '#3A9188' }
          }
        }}
      />

      <TextField
        fullWidth
        label="Anchor Link Block ID (Optional)"
        value={category.anchorLinkBlockId || ''}
        onChange={(e) => handleCategoryChange('anchorLinkBlockId', e.target.value)}
        variant="outlined"
        placeholder="Block ID to link to"
        sx={{
          mb: 3,
          '& .MuiInputLabel-root': { color: '#A4B0BE' },
          '& .MuiOutlinedInput-root': {
            color: '#ffffff',
            '& fieldset': { borderColor: '#A4B0BE' },
            '&:hover fieldset': { borderColor: '#3A9188' },
            '&.Mui-focused fieldset': { borderColor: '#3A9188' }
          }
        }}
      />

      {/* Images */}
      <Typography variant="body1" sx={{ color: '#ffffff', mb: 2, fontWeight: 600 }}>
        Images for Carousel
      </Typography>
      
      {(category.images || []).map((image: any, imageIndex: number) => (
        <Box
          key={imageIndex}
          sx={{
            border: '1px solid #A4B0BE',
            borderRadius: 1,
            p: 2,
            mb: 2,
            bgcolor: '#34495E'
          }}
        >
          <Typography sx={{ color: '#A4B0BE', mb: 1 }}>Image {imageIndex + 1}</Typography>
          
          <Box sx={{ mb: 2 }}>
            {image.url ? (
              <Box sx={{ position: 'relative' }}>
                <img
                  src={image.url}
                  alt={image.caption}
                  style={{
                    width: '100%',
                    maxHeight: 200,
                    objectFit: 'cover',
                    borderRadius: 8
                  }}
                />
                <IconButton
                  onClick={() => handleImageChange(imageIndex, 'url', '')}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    bgcolor: 'rgba(0,0,0,0.6)',
                    color: '#fff',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' }
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            ) : (
              <Button
                variant="outlined"
                startIcon={<ImageIcon />}
                onClick={() => handleImagePickerOpen(`categories.${categoryIndex}.images.${imageIndex}.url`)}
                sx={{
                  borderColor: '#A4B0BE',
                  color: '#A4B0BE',
                  '&:hover': { borderColor: '#3A9188', color: '#3A9188' }
                }}
              >
                Select Image
              </Button>
            )}
          </Box>

          <TextField
            fullWidth
            label="Caption"
            value={image.caption || ''}
            onChange={(e) => handleImageChange(imageIndex, 'caption', e.target.value)}
            variant="outlined"
            sx={{
              mb: 1,
              '& .MuiInputLabel-root': { color: '#A4B0BE' },
              '& .MuiOutlinedInput-root': {
                color: '#ffffff',
                '& fieldset': { borderColor: '#A4B0BE' },
                '&:hover fieldset': { borderColor: '#3A9188' },
                '&.Mui-focused fieldset': { borderColor: '#3A9188' }
              }
            }}
          />

          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={<DeleteIcon />}
            onClick={() => handleImageRemove(imageIndex)}
            sx={{
              borderColor: '#A4B0BE',
              color: '#A4B0BE',
              '&:hover': { borderColor: '#ff1744', color: '#ff1744' }
            }}
          >
            Remove Image
          </Button>
        </Box>
      ))}

      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={handleImageAdd}
        sx={{
          mb: 2,
          borderColor: '#3A9188',
          color: '#3A9188',
          '&:hover': { borderColor: '#2ECC71', color: '#2ECC71' }
        }}
      >
        Add Image
      </Button>

      {/* Remove Category */}
      <Button
        variant="outlined"
        color="error"
        fullWidth
        startIcon={<DeleteIcon />}
        onClick={() => handleRemove(categoryIndex)}
        sx={{
          mt: 2,
          borderColor: '#A4B0BE',
          color: '#A4B0BE',
          '&:hover': { borderColor: '#ff1744', color: '#ff1744' }
        }}
      >
        Remove Category
      </Button>
    </Box>
  );
};

const renderGalleryImage = (image: any, index: number) => (
  <Box
    key={index}
    sx={{
      border: '1px solid #3A9188',
      borderRadius: 2,
      p: 2,
      mb: 2
    }}
  >
    <Typography sx={{ color: '#A4B0BE', mb: 1 }}>Image {index + 1}</Typography>
    
    {/* IMAGE PICKER */}
    <Box sx={{ my: 2 }}>
      {image.url ? (
        <Card sx={{ mb: 1, position: 'relative' }}>
          <CardMedia
            component="img"
            height="200"
            image={image.url}
            alt="Gallery"
            sx={{ objectFit: 'cover' }}
          />
          <IconButton
            size="small"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'background.paper',
              '&:hover': { bgcolor: 'error.light', color: 'white' }
            }}
            onClick={() => handleItemChange(index, 'url', '')}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Card>
      ) : null}
      <Button
        variant="outlined"
        startIcon={<ImageIcon />}
        onClick={() => {
          setCurrentImageField(`images.${index}.url`);
          setMediaPickerOpen(true);
        }}
        fullWidth
        sx={{
          justifyContent: 'flex-start',
          py: 1.5,
          color: '#A4B0BE',
          borderColor: '#A4B0BE',
          '&:hover': { borderColor: '#3A9188', color: '#3A9188' }
        }}
      >
        {image.url ? 'Change Image' : 'Choose Image'}
      </Button>
    </Box>

    {/* CAPTION */}
    <TextField
      fullWidth
      label="Caption (Optional)"
      value={image.caption || ''}
      onChange={(e) => handleItemChange(index, 'caption', e.target.value)}
      variant="outlined"
      margin="normal"
      sx={{
        '& .MuiInputLabel-root': { color: '#A4B0BE' },
        '& .MuiOutlinedInput-root': {
          color: '#ffffff',
          '& fieldset': { borderColor: '#A4B0BE' },
          '&:hover fieldset': { borderColor: '#3A9188' },
          '&.Mui-focused fieldset': { borderColor: '#3A9188' }
        }
      }}
    />
    
    <Button
      variant="outlined"
      color="error"
      startIcon={<DeleteIcon />}
      onClick={() => handleRemove(index)}
      sx={{
        mt: 1,
        borderColor: '#A4B0BE',
        color: '#A4B0BE',
        '&:hover': { borderColor: '#ff1744', color: '#ff1744' }
      }}
    >
      Remove Image
    </Button>
  </Box>
);

const renderMember = (member: any, index: number) => (
  <Box
    key={index}
    sx={{
      border: '1px solid #3A9188',
      borderRadius: 2,
      p: 2,
      mb: 2
    }}
  >
    <Typography sx={{ color: '#A4B0BE', mb: 1 }}>Team Member {index + 1}</Typography>
    
    <TextField
      fullWidth
      label="Name"
      value={member.name || ''}
      onChange={(e) => handleItemChange(index, 'name', e.target.value)}
      variant="outlined"
      margin="normal"
      sx={{
        '& .MuiInputLabel-root': { color: '#A4B0BE' },
        '& .MuiOutlinedInput-root': {
          color: '#ffffff',
          '& fieldset': { borderColor: '#A4B0BE' },
          '&:hover fieldset': { borderColor: '#3A9188' },
          '&.Mui-focused fieldset': { borderColor: '#3A9188' }
        }
      }}
    />

    <TextField
      fullWidth
      label="Role"
      value={member.role || ''}
      onChange={(e) => handleItemChange(index, 'role', e.target.value)}
      variant="outlined"
      margin="normal"
      sx={{
        '& .MuiInputLabel-root': { color: '#A4B0BE' },
        '& .MuiOutlinedInput-root': {
          color: '#ffffff',
          '& fieldset': { borderColor: '#A4B0BE' },
          '&:hover fieldset': { borderColor: '#3A9188' },
          '&.Mui-focused fieldset': { borderColor: '#3A9188' }
        }
      }}
    />
    
    <TextField
      fullWidth
      multiline
      rows={4}
      label="Bio"
      value={member.bio || ''}
      onChange={(e) => handleItemChange(index, 'bio', e.target.value)}
      variant="outlined"
      margin="normal"
      sx={{
        '& .MuiInputLabel-root': { color: '#A4B0BE' },
        '& .MuiOutlinedInput-root': {
          color: '#ffffff',
          '& fieldset': { borderColor: '#A4B0BE' },
          '&:hover fieldset': { borderColor: '#3A9188' },
          '&.Mui-focused fieldset': { borderColor: '#3A9188' }
        }
      }}
    />

    {/* IMAGE PICKER */}
    <Box sx={{ my: 2 }}>
      <Typography variant="body2" gutterBottom sx={{ color: '#ffffff' }}>
        Photo
      </Typography>
      {member.image ? (
        <Card sx={{ mb: 1, position: 'relative' }}>
          <CardMedia
            component="img"
            height="200"
            image={member.image}
            alt="Team member"
            sx={{ objectFit: 'cover' }}
          />
          <IconButton
            size="small"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'background.paper',
              '&:hover': { bgcolor: 'error.light', color: 'white' }
            }}
            onClick={() => handleItemChange(index, 'image', '')}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Card>
      ) : null}
      <Button
        variant="outlined"
        startIcon={<ImageIcon />}
        onClick={() => {
          setCurrentImageField(`members.${index}.image`);
          setMediaPickerOpen(true);
        }}
        fullWidth
        sx={{
          justifyContent: 'flex-start',
          py: 1.5,
          color: '#A4B0BE',
          borderColor: '#A4B0BE',
          '&:hover': { borderColor: '#3A9188', color: '#3A9188' }
        }}
      >
        {member.image ? 'Change Photo' : 'Choose Photo'}
      </Button>
    </Box>

    {/* CREDENTIALS */}
    <Typography sx={{ color: '#A4B0BE', mt: 2, mb: 1 }}>Credentials</Typography>
    {(member.credentials || []).map((credential: string, credIdx: number) => (
      <Box key={credIdx} sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
        <TextField
          fullWidth
          value={credential}
          onChange={(e) => {
            const newCredentials = [...(member.credentials || [])];
            newCredentials[credIdx] = e.target.value;
            handleItemChange(index, 'credentials', newCredentials);
          }}
          variant="outlined"
          placeholder="PhD in Marine Biology"
          sx={{
            '& .MuiInputLabel-root': { color: '#A4B0BE' },
            '& .MuiOutlinedInput-root': {
              color: '#ffffff',
              '& fieldset': { borderColor: '#A4B0BE' },
              '&:hover fieldset': { borderColor: '#3A9188' },
              '&.Mui-focused fieldset': { borderColor: '#3A9188' }
            }
          }}
        />
        <IconButton 
          onClick={() => {
            const newCredentials = (member.credentials || []).filter((_: any, i: number) => i !== credIdx);
            handleItemChange(index, 'credentials', newCredentials);
          }} 
          sx={{ color: '#A4B0BE' }}
        >
          <DeleteIcon />
        </IconButton>
      </Box>
    ))}
    <Button
      variant="outlined"
      startIcon={<AddIcon />}
      onClick={() => {
        const newCredentials = [...(member.credentials || []), ''];
        handleItemChange(index, 'credentials', newCredentials);
      }}
      sx={{
        mt: 1,
        borderColor: '#3A9188',
        color: '#3A9188',
        '&:hover': { borderColor: '#2ECC71', color: '#2ECC71' }
      }}
    >
      Add Credential
    </Button>

    <TextField
      fullWidth
      label="LinkedIn URL (Optional)"
      value={member.linkedin || ''}
      onChange={(e) => handleItemChange(index, 'linkedin', e.target.value)}
      variant="outlined"
      margin="normal"
      sx={{
        '& .MuiInputLabel-root': { color: '#A4B0BE' },
        '& .MuiOutlinedInput-root': {
          color: '#ffffff',
          '& fieldset': { borderColor: '#A4B0BE' },
          '&:hover fieldset': { borderColor: '#3A9188' },
          '&.Mui-focused fieldset': { borderColor: '#3A9188' }
        }
      }}
    />
    
    <Button
      variant="outlined"
      color="error"
      startIcon={<DeleteIcon />}
      onClick={() => handleRemove(index)}
      sx={{
        mt: 1,
        borderColor: '#A4B0BE',
        color: '#A4B0BE',
        '&:hover': { borderColor: '#ff1744', color: '#ff1744' }
      }}
    >
      Remove Member
    </Button>
  </Box>
);

const renderAdvisor = (advisor: any, index: number) => (
  <Box
    key={index}
    sx={{
      border: '1px solid #3A9188',
      borderRadius: 2,
      p: 2,
      mb: 2
    }}
  >
    <Typography sx={{ color: '#A4B0BE', mb: 1 }}>Advisor {index + 1}</Typography>
    
    <TextField
      fullWidth
      label="Name"
      value={advisor.name || ''}
      onChange={(e) => handleItemChange(index, 'name', e.target.value)}
      variant="outlined"
      margin="normal"
      sx={{
        '& .MuiInputLabel-root': { color: '#A4B0BE' },
        '& .MuiOutlinedInput-root': {
          color: '#ffffff',
          '& fieldset': { borderColor: '#A4B0BE' },
          '&:hover fieldset': { borderColor: '#3A9188' },
          '&.Mui-focused fieldset': { borderColor: '#3A9188' }
        }
      }}
    />

    <TextField
      fullWidth
      label="Expertise"
      value={advisor.expertise || ''}
      onChange={(e) => handleItemChange(index, 'expertise', e.target.value)}
      variant="outlined"
      margin="normal"
      sx={{
        '& .MuiInputLabel-root': { color: '#A4B0BE' },
        '& .MuiOutlinedInput-root': {
          color: '#ffffff',
          '& fieldset': { borderColor: '#A4B0BE' },
          '&:hover fieldset': { borderColor: '#3A9188' },
          '&.Mui-focused fieldset': { borderColor: '#3A9188' }
        }
      }}
    />
    
    <TextField
      fullWidth
      multiline
      rows={3}
      label="Bio (Optional)"
      value={advisor.bio || ''}
      onChange={(e) => handleItemChange(index, 'bio', e.target.value)}
      variant="outlined"
      margin="normal"
      sx={{
        '& .MuiInputLabel-root': { color: '#A4B0BE' },
        '& .MuiOutlinedInput-root': {
          color: '#ffffff',
          '& fieldset': { borderColor: '#A4B0BE' },
          '&:hover fieldset': { borderColor: '#3A9188' },
          '&.Mui-focused fieldset': { borderColor: '#3A9188' }
        }
      }}
    />

    {/* IMAGE PICKER */}
    <Box sx={{ my: 2 }}>
      <Typography variant="body2" gutterBottom sx={{ color: '#ffffff' }}>
        Photo
      </Typography>
      {advisor.image ? (
        <Card sx={{ mb: 1, position: 'relative' }}>
          <CardMedia
            component="img"
            height="200"
            image={advisor.image}
            alt="Advisor"
            sx={{ objectFit: 'cover' }}
          />
          <IconButton
            size="small"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'background.paper',
              '&:hover': { bgcolor: 'error.light', color: 'white' }
            }}
            onClick={() => handleItemChange(index, 'image', '')}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Card>
      ) : null}
      <Button
        variant="outlined"
        startIcon={<ImageIcon />}
        onClick={() => {
          setCurrentImageField(`advisors.${index}.image`);
          setMediaPickerOpen(true);
        }}
        fullWidth
        sx={{
          justifyContent: 'flex-start',
          py: 1.5,
          color: '#A4B0BE',
          borderColor: '#A4B0BE',
          '&:hover': { borderColor: '#3A9188', color: '#3A9188' }
        }}
      >
        {advisor.image ? 'Change Photo' : 'Choose Photo'}
      </Button>
    </Box>
    
    <Button
      variant="outlined"
      color="error"
      startIcon={<DeleteIcon />}
      onClick={() => handleRemove(index)}
      sx={{
        mt: 1,
        borderColor: '#A4B0BE',
        color: '#A4B0BE',
        '&:hover': { borderColor: '#ff1744', color: '#ff1744' }
      }}
    >
      Remove Advisor
    </Button>
  </Box>
);


const renderSocialLink = (link: any, linkIndex: number) => {
  const handleLinkChange = (field: string, val: any) => {
    const newLinks = [...value];
    newLinks[linkIndex] = { ...newLinks[linkIndex], [field]: val };
    handleFieldChange('socialLinks', newLinks);
  };

  return (
    <Box
      key={linkIndex}
      sx={{
        border: '2px solid #3A9188',
        borderRadius: 2,
        p: 3,
        mb: 3,
        bgcolor: '#2C3E50'
      }}
    >
      <Typography variant="h6" sx={{ color: '#ffffff', mb: 2 }}>
        Social Link {linkIndex + 1}
      </Typography>

      {/* Platform */}
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel sx={{ color: '#A4B0BE' }}>Platform</InputLabel>
        <Select
          value={link.platform || 'instagram'}
          onChange={(e) => handleLinkChange('platform', e.target.value)}
          sx={{
            color: '#ffffff',
            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#A4B0BE' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#3A9188' },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3A9188' }
          }}
        >
          <MenuItem value="instagram">Instagram</MenuItem>
          <MenuItem value="facebook">Facebook</MenuItem>
          <MenuItem value="youtube">YouTube</MenuItem>
          <MenuItem value="twitter">Twitter/X</MenuItem>
          <MenuItem value="linkedin">LinkedIn</MenuItem>
          <MenuItem value="tiktok">TikTok</MenuItem>
          <MenuItem value="spotify">Spotify</MenuItem>
          <MenuItem value="bandcamp">Bandcamp</MenuItem>
          <MenuItem value="soundcloud">SoundCloud</MenuItem>
          <MenuItem value="custom">Custom</MenuItem>
        </Select>
      </FormControl>

      {/* URL */}
      <TextField
        fullWidth
        label="Profile URL"
        value={link.url || ''}
        onChange={(e) => handleLinkChange('url', e.target.value)}
        variant="outlined"
        placeholder="https://instagram.com/yourusername"
        sx={{
          mb: 2,
          '& .MuiInputLabel-root': { color: '#A4B0BE' },
          '& .MuiOutlinedInput-root': {
            color: '#ffffff',
            '& fieldset': { borderColor: '#A4B0BE' },
            '&:hover fieldset': { borderColor: '#3A9188' },
            '&.Mui-focused fieldset': { borderColor: '#3A9188' }
          }
        }}
      />

      {/* Handle */}
      <TextField
        fullWidth
        label="Handle/Username (Optional)"
        value={link.handle || ''}
        onChange={(e) => handleLinkChange('handle', e.target.value)}
        variant="outlined"
        placeholder="@yourusername"
        sx={{
          mb: 2,
          '& .MuiInputLabel-root': { color: '#A4B0BE' },
          '& .MuiOutlinedInput-root': {
            color: '#ffffff',
            '& fieldset': { borderColor: '#A4B0BE' },
            '&:hover fieldset': { borderColor: '#3A9188' },
            '&.Mui-focused fieldset': { borderColor: '#3A9188' }
          }
        }}
      />

      {/* Follower Count */}
      <TextField
        fullWidth
        label="Follower Count Display (Optional)"
        value={link.followerCount || ''}
        onChange={(e) => handleLinkChange('followerCount', e.target.value)}
        variant="outlined"
        placeholder="12.5K followers"
        helperText="Display text shown below the icon (e.g., '12.5K followers')"
        sx={{
          mb: 2,
          '& .MuiInputLabel-root': { color: '#A4B0BE' },
          '& .MuiOutlinedInput-root': {
            color: '#ffffff',
            '& fieldset': { borderColor: '#A4B0BE' },
            '&:hover fieldset': { borderColor: '#3A9188' },
            '&.Mui-focused fieldset': { borderColor: '#3A9188' }
          },
          '& .MuiFormHelperText-root': { color: '#A4B0BE' }
        }}
      />

      {/* Custom Icon/Label for Custom Platform */}
      {link.platform === 'custom' && (
        <>
          <TextField
            fullWidth
            label="Custom Platform Label"
            value={link.customLabel || ''}
            onChange={(e) => handleLinkChange('customLabel', e.target.value)}
            variant="outlined"
            placeholder="Website"
            sx={{
              mb: 2,
              '& .MuiInputLabel-root': { color: '#A4B0BE' },
              '& .MuiOutlinedInput-root': {
                color: '#ffffff',
                '& fieldset': { borderColor: '#A4B0BE' },
                '&:hover fieldset': { borderColor: '#3A9188' },
                '&.Mui-focused fieldset': { borderColor: '#3A9188' }
              }
            }}
          />
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ color: '#A4B0BE', mb: 1 }}>
              Custom Icon (Optional)
            </Typography>
            {link.customIcon ? (
              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                <img
                  src={link.customIcon}
                  alt="Custom icon"
                  style={{ width: 64, height: 64, borderRadius: 8 }}
                />
                <IconButton
                  onClick={() => handleLinkChange('customIcon', '')}
                  sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    bgcolor: 'rgba(0,0,0,0.6)',
                    color: '#fff',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' }
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : null}
            <Button
              variant="outlined"
              startIcon={<ImageIcon />}
              onClick={() => handleImagePickerOpen(`socialLinks.${linkIndex}.customIcon`)}
              sx={{
                mt: link.customIcon ? 1 : 0,
                borderColor: '#A4B0BE',
                color: '#A4B0BE',
                '&:hover': { borderColor: '#3A9188', color: '#3A9188' }
              }}
            >
              {link.customIcon ? 'Change Icon' : 'Upload Icon'}
            </Button>
          </Box>
        </>
      )}

      {/* Remove */}
      <Button
        variant="outlined"
        color="error"
        fullWidth
        startIcon={<DeleteIcon />}
        onClick={() => handleRemove(linkIndex)}
        sx={{
          mt: 2,
          borderColor: '#A4B0BE',
          color: '#A4B0BE',
          '&:hover': { borderColor: '#ff1744', color: '#ff1744' }
        }}
      >
        Remove Social Link
      </Button>
    </Box>
  );
};

const renderResourceLink = (link: any, linkIndex: number) => {
  const handleLinkChange = (field: string, val: any) => {
    const newLinks = [...value];
    newLinks[linkIndex] = { ...newLinks[linkIndex], [field]: val };
    handleFieldChange('resourceLinks', newLinks);
  };

  return (
    <Box
      key={linkIndex}
      sx={{
        border: '2px solid #3A9188',
        borderRadius: 2,
        p: 3,
        mb: 3,
        bgcolor: '#2C3E50'
      }}
    >
      <Typography variant="h6" sx={{ color: '#ffffff', mb: 2 }}>
        Resource Link {linkIndex + 1}
      </Typography>

      {/* Title */}
      <TextField
        fullWidth
        label="Title"
        value={link.title || ''}
        onChange={(e) => handleLinkChange('title', e.target.value)}
        variant="outlined"
        placeholder="Book Tickets"
        sx={{
          mb: 2,
          '& .MuiInputLabel-root': { color: '#A4B0BE' },
          '& .MuiOutlinedInput-root': {
            color: '#ffffff',
            '& fieldset': { borderColor: '#A4B0BE' },
            '&:hover fieldset': { borderColor: '#3A9188' },
            '&.Mui-focused fieldset': { borderColor: '#3A9188' }
          }
        }}
      />

      {/* Description */}
      <TextField
        fullWidth
        multiline
        rows={2}
        label="Description (Optional)"
        value={link.description || ''}
        onChange={(e) => handleLinkChange('description', e.target.value)}
        variant="outlined"
        placeholder="Reserve your seat for our next performance"
        sx={{
          mb: 2,
          '& .MuiInputLabel-root': { color: '#A4B0BE' },
          '& .MuiOutlinedInput-root': {
            color: '#ffffff',
            '& fieldset': { borderColor: '#A4B0BE' },
            '&:hover fieldset': { borderColor: '#3A9188' },
            '&.Mui-focused fieldset': { borderColor: '#3A9188' }
          }
        }}
      />

      {/* URL */}
      <TextField
        fullWidth
        label="Link URL"
        value={link.url || ''}
        onChange={(e) => handleLinkChange('url', e.target.value)}
        variant="outlined"
        placeholder="https://..."
        sx={{
          mb: 2,
          '& .MuiInputLabel-root': { color: '#A4B0BE' },
          '& .MuiOutlinedInput-root': {
            color: '#ffffff',
            '& fieldset': { borderColor: '#A4B0BE' },
            '&:hover fieldset': { borderColor: '#3A9188' },
            '&.Mui-focused fieldset': { borderColor: '#3A9188' }
          }
        }}
      />

      {/* Icon */}
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel sx={{ color: '#A4B0BE' }}>Icon</InputLabel>
        <Select
          value={link.icon || 'document'}
          onChange={(e) => handleLinkChange('icon', e.target.value)}
          sx={{
            color: '#ffffff',
            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#A4B0BE' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#3A9188' },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3A9188' }
          }}
        >
          <MenuItem value="ticket">🎫 Ticket</MenuItem>
          <MenuItem value="download">📥 Download</MenuItem>
          <MenuItem value="email">📧 Email</MenuItem>
          <MenuItem value="phone">📞 Phone</MenuItem>
          <MenuItem value="calendar">📅 Calendar</MenuItem>
          <MenuItem value="document">📄 Document</MenuItem>
          <MenuItem value="partnership">🤝 Partnership</MenuItem>
          <MenuItem value="press">📰 Press</MenuItem>
          <MenuItem value="custom">🖼️ Custom</MenuItem>
        </Select>
      </FormControl>

      {/* Custom Icon Upload */}
      {link.icon === 'custom' && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ color: '#A4B0BE', mb: 1 }}>
            Custom Icon Image
          </Typography>
          {link.customIcon ? (
            <Box sx={{ position: 'relative', display: 'inline-block' }}>
              <img
                src={link.customIcon}
                alt="Custom icon"
                style={{ width: 64, height: 64, borderRadius: 8 }}
              />
              <IconButton
                onClick={() => handleLinkChange('customIcon', '')}
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  bgcolor: 'rgba(0,0,0,0.6)',
                  color: '#fff',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' }
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ) : null}
          <Button
            variant="outlined"
            startIcon={<ImageIcon />}
            onClick={() => handleImagePickerOpen(`resourceLinks.${linkIndex}.customIcon`)}
            sx={{
              mt: link.customIcon ? 1 : 0,
              borderColor: '#A4B0BE',
              color: '#A4B0BE',
              '&:hover': { borderColor: '#3A9188', color: '#3A9188' }
            }}
          >
            {link.customIcon ? 'Change Icon' : 'Upload Icon'}
          </Button>
        </Box>
      )}

      {/* CTA Text */}
      <TextField
        fullWidth
        label="Button Text"
        value={link.ctaText || ''}
        onChange={(e) => handleLinkChange('ctaText', e.target.value)}
        variant="outlined"
        placeholder="Book Now"
        sx={{
          mb: 2,
          '& .MuiInputLabel-root': { color: '#A4B0BE' },
          '& .MuiOutlinedInput-root': {
            color: '#ffffff',
            '& fieldset': { borderColor: '#A4B0BE' },
            '&:hover fieldset': { borderColor: '#3A9188' },
            '&.Mui-focused fieldset': { borderColor: '#3A9188' }
          }
        }}
      />

      {/* Open in New Tab */}
      <FormControlLabel
        control={
          <Switch
            checked={link.openInNewTab !== false}
            onChange={(e) => handleLinkChange('openInNewTab', e.target.checked)}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': {
                color: '#3A9188'
              },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                bgcolor: '#3A9188'
              }
            }}
          />
        }
        label="Open in New Tab"
        sx={{
          color: '#ffffff',
          mb: 2
        }}
      />

      {/* Background Color */}
      <TextField
        fullWidth
        label="Card Background Color (Optional)"
        value={link.backgroundColor || ''}
        onChange={(e) => handleLinkChange('backgroundColor', e.target.value)}
        variant="outlined"
        placeholder="#3A9188"
        helperText="Hex color code for card background"
        sx={{
          mb: 2,
          '& .MuiInputLabel-root': { color: '#A4B0BE' },
          '& .MuiOutlinedInput-root': {
            color: '#ffffff',
            '& fieldset': { borderColor: '#A4B0BE' },
            '&:hover fieldset': { borderColor: '#3A9188' },
            '&.Mui-focused fieldset': { borderColor: '#3A9188' }
          },
          '& .MuiFormHelperText-root': { color: '#A4B0BE' }
        }}
      />

      {/* Remove */}
      <Button
        variant="outlined"
        color="error"
        fullWidth
        startIcon={<DeleteIcon />}
        onClick={() => handleRemove(linkIndex)}
        sx={{
          mt: 2,
          borderColor: '#A4B0BE',
          color: '#A4B0BE',
          '&:hover': { borderColor: '#ff1744', color: '#ff1744' }
        }}
      >
        Remove Resource Link
      </Button>
    </Box>
  );
};

const renderVideo = (video: any, videoIndex: number) => {
  const handleVideoChange = (field: string, val: any) => {
    const newVideos = [...value];
    newVideos[videoIndex] = { ...newVideos[videoIndex], [field]: val };
    handleFieldChange('videos', newVideos);
  };

  // Extract video ID from YouTube/Vimeo URL
  const getVideoPreviewUrl = (url: string) => {
    if (!url) return null;
    
    // YouTube
    const ytMatch = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    if (ytMatch) {
      return `https://img.youtube.com/vi/${ytMatch[1]}/hqdefault.jpg`;
    }
    
    // Vimeo - basic pattern (note: requires API call for actual thumbnail)
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) {
      return `https://vumbnail.com/${vimeoMatch[1]}.jpg`;
    }
    
    return null;
  };

  const previewUrl = video.thumbnail || getVideoPreviewUrl(video.url);

  return (
    <Box
      key={videoIndex}
      sx={{
        border: '2px solid #3A9188',
        borderRadius: 2,
        p: 3,
        mb: 3,
        bgcolor: '#2C3E50'
      }}
    >
      <Typography variant="h6" sx={{ color: '#ffffff', mb: 2 }}>
        Video {videoIndex + 1}
      </Typography>

      {/* Video URL */}
      <TextField
        fullWidth
        label="Video URL (YouTube or Vimeo)"
        value={video.url || ''}
        onChange={(e) => handleVideoChange('url', e.target.value)}
        variant="outlined"
        placeholder="https://www.youtube.com/watch?v=..."
        sx={{
          mb: 2,
          '& .MuiInputLabel-root': { color: '#A4B0BE' },
          '& .MuiOutlinedInput-root': {
            color: '#ffffff',
            '& fieldset': { borderColor: '#A4B0BE' },
            '&:hover fieldset': { borderColor: '#3A9188' },
            '&.Mui-focused fieldset': { borderColor: '#3A9188' }
          }
        }}
      />

      {/* Preview Thumbnail */}
      {previewUrl && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ color: '#A4B0BE', mb: 1 }}>
            Preview:
          </Typography>
          <img
            src={previewUrl}
            alt="Video thumbnail"
            style={{
              width: '100%',
              maxWidth: 400,
              height: 'auto',
              borderRadius: 8,
              border: '1px solid #A4B0BE'
            }}
          />
        </Box>
      )}

      {/* Video Title */}
      <TextField
        fullWidth
        label="Video Title"
        value={video.title || ''}
        onChange={(e) => handleVideoChange('title', e.target.value)}
        variant="outlined"
        sx={{
          mb: 2,
          '& .MuiInputLabel-root': { color: '#A4B0BE' },
          '& .MuiOutlinedInput-root': {
            color: '#ffffff',
            '& fieldset': { borderColor: '#A4B0BE' },
            '&:hover fieldset': { borderColor: '#3A9188' },
            '&.Mui-focused fieldset': { borderColor: '#3A9188' }
          }
        }}
      />

      {/* Video Description */}
      <TextField
        fullWidth
        multiline
        rows={3}
        label="Description (Optional)"
        value={video.description || ''}
        onChange={(e) => handleVideoChange('description', e.target.value)}
        variant="outlined"
        sx={{
          mb: 2,
          '& .MuiInputLabel-root': { color: '#A4B0BE' },
          '& .MuiOutlinedInput-root': {
            color: '#ffffff',
            '& fieldset': { borderColor: '#A4B0BE' },
            '&:hover fieldset': { borderColor: '#3A9188' },
            '&.Mui-focused fieldset': { borderColor: '#3A9188' }
          }
        }}
      />

      {/* Metric Callout */}
      <TextField
        fullWidth
        label="Metric Callout (Optional)"
        value={video.metricCallout || ''}
        onChange={(e) => handleVideoChange('metricCallout', e.target.value)}
        variant="outlined"
        placeholder="e.g., 12K views or Featured Artist"
        sx={{
          mb: 2,
          '& .MuiInputLabel-root': { color: '#A4B0BE' },
          '& .MuiOutlinedInput-root': {
            color: '#ffffff',
            '& fieldset': { borderColor: '#A4B0BE' },
            '&:hover fieldset': { borderColor: '#3A9188' },
            '&.Mui-focused fieldset': { borderColor: '#3A9188' }
          }
        }}
      />

      {/* Category */}
      <TextField
        fullWidth
        label="Category Tag (Optional)"
        value={video.category || ''}
        onChange={(e) => handleVideoChange('category', e.target.value)}
        variant="outlined"
        placeholder="e.g., Performance, Testimonial, Behind the Scenes"
        sx={{
          mb: 2,
          '& .MuiInputLabel-root': { color: '#A4B0BE' },
          '& .MuiOutlinedInput-root': {
            color: '#ffffff',
            '& fieldset': { borderColor: '#A4B0BE' },
            '&:hover fieldset': { borderColor: '#3A9188' },
            '&.Mui-focused fieldset': { borderColor: '#3A9188' }
          }
        }}
      />

      {/* CTA Text */}
      <TextField
        fullWidth
        label="CTA Button Text (Optional)"
        value={video.ctaText || ''}
        onChange={(e) => handleVideoChange('ctaText', e.target.value)}
        variant="outlined"
        placeholder="e.g., Book This Experience"
        sx={{
          mb: 2,
          '& .MuiInputLabel-root': { color: '#A4B0BE' },
          '& .MuiOutlinedInput-root': {
            color: '#ffffff',
            '& fieldset': { borderColor: '#A4B0BE' },
            '&:hover fieldset': { borderColor: '#3A9188' },
            '&.Mui-focused fieldset': { borderColor: '#3A9188' }
          }
        }}
      />

      {/* CTA Link */}
      <TextField
        fullWidth
        label="CTA Button Link (Optional)"
        value={video.ctaLink || ''}
        onChange={(e) => handleVideoChange('ctaLink', e.target.value)}
        variant="outlined"
        placeholder="https://..."
        sx={{
          mb: 2,
          '& .MuiInputLabel-root': { color: '#A4B0BE' },
          '& .MuiOutlinedInput-root': {
            color: '#ffffff',
            '& fieldset': { borderColor: '#A4B0BE' },
            '&:hover fieldset': { borderColor: '#3A9188' },
            '&.Mui-focused fieldset': { borderColor: '#3A9188' }
          }
        }}
      />

      {/* Custom Thumbnail Upload (Optional) */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ color: '#A4B0BE', mb: 1 }}>
          Custom Thumbnail (Optional - overrides auto-detected)
        </Typography>
        {video.thumbnail && !getVideoPreviewUrl(video.url) && (
          <Box sx={{ position: 'relative', mb: 1 }}>
            <img
              src={video.thumbnail}
              alt="Custom thumbnail"
              style={{
                width: '100%',
                maxWidth: 400,
                height: 'auto',
                borderRadius: 8
              }}
            />
            <IconButton
              onClick={() => handleVideoChange('thumbnail', '')}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                bgcolor: 'rgba(0,0,0,0.6)',
                color: '#fff',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        )}
        <Button
          variant="outlined"
          startIcon={<ImageIcon />}
          onClick={() => handleImagePickerOpen(`videos.${videoIndex}.thumbnail`)}
          sx={{
            borderColor: '#A4B0BE',
            color: '#A4B0BE',
            '&:hover': { borderColor: '#3A9188', color: '#3A9188' }
          }}
        >
          {video.thumbnail ? 'Change Thumbnail' : 'Upload Custom Thumbnail'}
        </Button>
      </Box>

      {/* Remove Video */}
      <Button
        variant="outlined"
        color="error"
        fullWidth
        startIcon={<DeleteIcon />}
        onClick={() => handleRemove(videoIndex)}
        sx={{
          mt: 2,
          borderColor: '#A4B0BE',
          color: '#A4B0BE',
          '&:hover': { borderColor: '#ff1744', color: '#ff1744' }
        }}
      >
        Remove Video
      </Button>
    </Box>
  );
};

    const renderOffering = (offering: any, index: number) => (
      <Box
        key={index}
        sx={{
          border: '1px solid #3A9188',
          borderRadius: 2,
          p: 2,
          mb: 2
        }}
      >
        <Typography sx={{ color: '#A4B0BE', mb: 1 }}>Offering {index + 1}</Typography>
        
        <TextField
          fullWidth
          label="Title"
          value={offering.title || ''}
          onChange={(e) => handleItemChange(index, 'title', e.target.value)}
          variant="outlined"
          margin="normal"
          sx={{
            '& .MuiInputLabel-root': { color: '#A4B0BE' },
            '& .MuiOutlinedInput-root': {
              color: '#ffffff',
              '& fieldset': { borderColor: '#A4B0BE' },
              '&:hover fieldset': { borderColor: '#3A9188' },
              '&.Mui-focused fieldset': { borderColor: '#3A9188' }
            }
          }}
        />
        
<Box sx={{ mt: 2, mb: 2 }}>
  <Typography variant="body2" sx={{ color: '#A4B0BE', mb: 1 }}>
    Description
  </Typography>
  <RichTextEditor
    value={offering.description || ''}
    onChange={(html) => handleItemChange(index, 'description', html)}
  />
</Box>

        {/* Highlights array */}
        <Typography sx={{ color: '#A4B0BE', mt: 2, mb: 1 }}>Highlights</Typography>
        {(offering.highlights || []).map((highlight: string, hIdx: number) => (
          <Box key={hIdx} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <TextField
              fullWidth
              value={highlight}
              onChange={(e) => {
                const newHighlights = [...(offering.highlights || [])];
                newHighlights[hIdx] = e.target.value;
                handleItemChange(index, 'highlights', newHighlights);
              }}
              variant="outlined"
              placeholder="Highlight benefit"
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: '#ffffff',
                  '& fieldset': { borderColor: '#A4B0BE' },
                  '&:hover fieldset': { borderColor: '#3A9188' },
                  '&.Mui-focused fieldset': { borderColor: '#3A9188' }
                }
              }}
            />
            <IconButton
              onClick={() => {
                const newHighlights = (offering.highlights || []).filter(
                  (_: string, i: number) => i !== hIdx
                );
                handleItemChange(index, 'highlights', newHighlights);
              }}
              sx={{ ml: 1, color: '#A4B0BE' }}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        ))}
        <Button
          variant="text"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => {
            const newHighlights = [...(offering.highlights || []), ''];
            handleItemChange(index, 'highlights', newHighlights);
          }}
          sx={{
            mb: 2,
            color: '#A4B0BE',
            '&:hover': { color: '#3A9188' }
          }}
        >
          Add Highlight
        </Button>

        <FormControl fullWidth margin="normal">
          <InputLabel sx={{ color: '#A4B0BE' }}>Pricing Model</InputLabel>
          <Select
            value={offering.pricingModel || 'fixed'}
            onChange={(e) => handleItemChange(index, 'pricingModel', e.target.value)}
            sx={{
              color: '#ffffff',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#A4B0BE' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#3A9188' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3A9188' },
              '& .MuiSvgIcon-root': { color: '#A4B0BE' }
            }}
          >
            <MenuItem value="fixed">Fixed Price</MenuItem>
            <MenuItem value="tiered">Tiered</MenuItem>
            <MenuItem value="custom">Custom</MenuItem>
            <MenuItem value="negotiable">Negotiable</MenuItem>
            <MenuItem value="in_kind">In-Kind</MenuItem>
          </Select>
        </FormControl>

        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={() => handleRemove(index)}
          sx={{
            mt: 1,
            borderColor: '#A4B0BE',
            color: '#A4B0BE',
            '&:hover': { borderColor: '#ff1744', color: '#ff1744' }
          }}
        >
          Remove Offering
        </Button>
      </Box>
    );

    const renderSimpleStringList = (arr: any[]) =>
      arr.map((item: string, i: number) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <TextField
            fullWidth
            value={item}
            onChange={(e) => {
              const newArr = [...arr];
              newArr[i] = e.target.value;
              handleFieldChange(field.name, newArr);
            }}
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                color: '#ffffff',
                '& fieldset': { borderColor: '#A4B0BE' },
                '&:hover fieldset': { borderColor: '#3A9188' },
                '&.Mui-focused fieldset': { borderColor: '#3A9188' }
              }
            }}
          />
          <IconButton onClick={() => handleRemove(i)} sx={{ ml: 1, color: '#A4B0BE' }}>
            <DeleteIcon />
          </IconButton>
        </Box>
      ));

    return (
      <Box key={field.name} sx={{ my: 3 }}>
        <Typography variant="h6" sx={{ color: '#ffffff', mb: 1 }}>
          {field.label}
        </Typography>

 {field.name === 'metrics' && blockType !== 'past_impact'
  ? value.map((metric: any, index: number) => renderMetric(metric, index))
  : field.name === 'metrics' && blockType === 'past_impact'
  ? value.map((metric: any, index: number) => renderPastImpactMetric(metric, index))
  : field.name === 'offerings'
  ? value.map((offering: any, index: number) => renderOffering(offering, index))
  : field.name === 'cases'
  ? value.map((caseStudy: any, index: number) => renderCaseStudy(caseStudy, index))
: field.name === 'testimonials'
? value.map((testimonial: any, index: number) => renderTestimonial(testimonial, index))
: field.name === 'members'
? value.map((member: any, index: number) => renderMember(member, index))
: field.name === 'advisors'
? value.map((advisor: any, index: number) => renderAdvisor(advisor, index))
: field.name === 'images'
  ? value.map((image: any, index: number) => renderGalleryImage(image, index))  // ← AND THIS
  : field.name === 'featuredArtists'
  ? value.map((artist: string, i: number) => renderFeaturedArtist(artist, i))
 : field.name === 'ageDistribution'
? (value?.data || []).map((item: any, index: number) => renderAgeDistribution(item, index))
: field.name === 'professionalProfile'
? (value?.data || []).map((item: any, index: number) => renderProfessionalProfile(item, index))
: field.name === 'geographicReach'
? (value?.data || []).map((item: any, index: number) => renderGeographicReach(item, index))
  : field.name === 'socialMedia'
  ? value.map((item: any, index: number) => renderSocialMedia(item, index))
  : field.name === 'pressCoverage'
  ? value.map((item: any, index: number) => renderPressCoverage(item, index))
    : field.name === 'categories'
  ? value.map((category: any, index: number) => renderCategory(category, index))
    : field.name === 'videos'
  ? value.map((video: any, index: number) => renderVideo(video, index))
    : field.name === 'socialLinks'
  ? value.map((link: any, index: number) => renderSocialLink(link, index))
  : field.name === 'resourceLinks'
  ? value.map((link: any, index: number) => renderResourceLink(link, index))
  : field.name === 'programs'
  ? value.map((program: any, index: number) => renderProgram(program, index))
  : field.name === 'values'
  ? value.map((item: string, i: number) => (
    <Box key={i} sx={{ mb: 2 }}>
      <Typography variant="body2" sx={{ color: '#ffffff', mb: 1 }}>
        Core Value {i + 1}
      </Typography>
      <RichTextEditor
        value={item}
        onChange={(html) => {
          const newArr = [...value];
          newArr[i] = html;
          handleFieldChange(field.name, newArr);
        }}
      />
      <Button
        variant="outlined"
        color="error"
        startIcon={<DeleteIcon />}
        onClick={() => handleRemove(i)}
        sx={{
          mt: 1,
          borderColor: '#A4B0BE',
          color: '#A4B0BE',
          '&:hover': { borderColor: '#ff1744', color: '#ff1744' }
        }}
      >
        Remove Value
      </Button>
    </Box>
  ))
  : field.name === 'mediaIds'
  ? renderSimpleStringList(value)
  : value.map((item: any, i: number) => (
              <Box
                key={i}
                sx={{
                  border: '1px solid #3A9188',
                  borderRadius: 2,
                  p: 2,
                  mb: 2
                }}
              >
                <Typography sx={{ color: '#A4B0BE', mb: 1 }}>
                  {`${field.label} ${i + 1}`}
                </Typography>
                {Object.keys(item).map((key) => (
                  <TextField
                    key={key}
                    fullWidth
                    label={key.charAt(0).toUpperCase() + key.slice(1)}
                    value={item[key]}
                    onChange={(e) => handleItemChange(i, key, e.target.value)}
                    variant="outlined"
                    margin="normal"
                    sx={{
                      '& .MuiInputLabel-root': { color: '#A4B0BE' },
                      '& .MuiOutlinedInput-root': {
                        color: '#ffffff',
                        '& fieldset': { borderColor: '#A4B0BE' },
                        '&:hover fieldset': { borderColor: '#3A9188' },
                        '&.Mui-focused fieldset': { borderColor: '#3A9188' }
                      }
                    }}
                  />
                ))}
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => handleRemove(i)}
                  sx={{
                    mt: 1,
                    borderColor: '#A4B0BE',
                    color: '#A4B0BE',
                    '&:hover': { borderColor: '#ff1744', color: '#ff1744' }
                  }}
                >
                  Remove
                </Button>
              </Box>
            ))}

        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          sx={{
            mt: 1,
            borderColor: '#A4B0BE',
            color: '#A4B0BE',
            '&:hover': { borderColor: '#3A9188', color: '#3A9188' }
          }}
        >
          + Add Another
        </Button>
      </Box>
    );
  };

  const renderField = (field: any) => {
    const value = blockData[field.name] ?? '';

    // handle array fields first
    if (arrayFieldNames.includes(field.name)) {
      return renderArrayField(field);
    }

    switch (field.type) {
      case 'text':
        return (
          <TextField
            key={field.name}
            fullWidth
            label={field.label}
            value={value}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            required={field.required}
            helperText={field.helpText}
            variant="outlined"
            margin="normal"
            sx={{
              '& .MuiInputLabel-root': { color: '#A4B0BE' },
              '& .MuiOutlinedInput-root': {
                color: '#ffffff',
                '& fieldset': { borderColor: '#A4B0BE' },
                '&:hover fieldset': { borderColor: '#3A9188' },
                '&.Mui-focused fieldset': { borderColor: '#3A9188' }
              }
            }}
          />
        );

      case 'textarea':
        return (
          <TextField
            key={field.name}
            fullWidth
            multiline
            rows={4}
            label={field.label}
            value={value}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            required={field.required}
            helperText={field.helpText}
            variant="outlined"
            margin="normal"
            sx={{
              '& .MuiInputLabel-root': { color: '#A4B0BE' },
              '& .MuiOutlinedInput-root': {
                color: '#ffffff',
                '& fieldset': { borderColor: '#A4B0BE' },
                '&:hover fieldset': { borderColor: '#3A9188' },
                '&.Mui-focused fieldset': { borderColor: '#3A9188' }
              }
            }}
          />
        );

case 'artistSelector':
  return (
    <Box key={field.name} sx={{ my: 3 }}>
      <Typography variant="h6" sx={{ color: '#ffffff', mb: 1 }}>
        {field.label}
      </Typography>
      {renderArtistSelector(field, value || [])} 
      {field.helpText && (
        <FormHelperText sx={{ color: '#A4B0BE' }}>{field.helpText}</FormHelperText>
      )}
    </Box>
  );

case 'number':
  // Handle nested fields like 'satisfactionRate.percentage'
  const fieldPath = field.name.split('.');
  const fieldValue = fieldPath.reduce((obj: any, key: string) => obj?.[key], blockData) ?? '';
  
  return (
    <TextField
      key={field.name}
      fullWidth
      type="number"
      label={field.label}
      value={fieldValue}
      onChange={(e) => {
        if (fieldPath.length > 1) {
          // Handle nested object
          const newData = { ...blockData };
          let current: any = newData;
          for (let i = 0; i < fieldPath.length - 1; i++) {
            if (!current[fieldPath[i]]) current[fieldPath[i]] = {};
            current = current[fieldPath[i]];
          }
          current[fieldPath[fieldPath.length - 1]] = parseFloat(e.target.value) || 0;
          onChange(newData);
        } else {
          handleFieldChange(field.name, parseFloat(e.target.value));
        }
      }}
      required={field.required}
      helperText={field.helpText}
      variant="outlined"
      margin="normal"
      inputProps={{ min: field.min, max: field.max, step: field.step }}
      sx={{
        '& .MuiInputLabel-root': { color: '#A4B0BE' },
        '& .MuiOutlinedInput-root': {
          color: '#ffffff',
          '& fieldset': { borderColor: '#A4B0BE' },
          '&:hover fieldset': { borderColor: '#3A9188' },
          '&.Mui-focused fieldset': { borderColor: '#3A9188' }
        }
      }}
    />
  );

      case 'toggle':
        return (
          <Box key={field.name} sx={{ my: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!value}
                  onChange={(e) => handleFieldChange(field.name, e.target.checked)}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#3A9188' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#3A9188'
                    }
                  }}
                />
              }
              label={<Typography sx={{ color: '#ffffff' }}>{field.label}</Typography>}
            />
            {field.helpText && (
              <FormHelperText sx={{ color: '#A4B0BE' }}>{field.helpText}</FormHelperText>
            )}
          </Box>
        );

      case 'slider':
        return (
          <Box key={field.name} sx={{ my: 3 }}>
            <Typography variant="body2" gutterBottom sx={{ color: '#ffffff' }}>
              {field.label}
            </Typography>
            <Slider
              value={typeof value === 'number' ? value : field.min || 0}
              onChange={(e, newValue) => handleFieldChange(field.name, newValue)}
              min={field.min || 0}
              max={field.max || 100}
              step={field.step || 1}
              marks
              valueLabelDisplay="auto"
              sx={{
                color: '#3A9188',
                '& .MuiSlider-thumb': { backgroundColor: '#3A9188' },
                '& .MuiSlider-track': { backgroundColor: '#3A9188' },
                '& .MuiSlider-rail': { backgroundColor: '#A4B0BE' }
              }}
            />
            {field.helpText && (
              <FormHelperText sx={{ color: '#A4B0BE' }}>{field.helpText}</FormHelperText>
            )}
          </Box>
        );

      case 'color':
        return (
          <TextField
            key={field.name}
            fullWidth
            type="color"
            label={field.label}
            value={value || '#000000'}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            required={field.required}
            helperText={field.helpText}
            variant="outlined"
            margin="normal"
            sx={{
              '& .MuiInputLabel-root': { color: '#A4B0BE' },
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: '#A4B0BE' },
                '&:hover fieldset': { borderColor: '#3A9188' },
                '&.Mui-focused fieldset': { borderColor: '#3A9188' }
              }
            }}
          />
        );

      case 'select':
        return (
          <FormControl key={field.name} fullWidth margin="normal" required={field.required}>
            <InputLabel sx={{ color: '#A4B0BE' }}>{field.label}</InputLabel>
            <Select
              value={value}
              onChange={(e) => handleFieldChange(field.name, e.target.value)}
              label={field.label}
              sx={{
                color: '#ffffff',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#A4B0BE' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#3A9188' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3A9188' },
                '& .MuiSvgIcon-root': { color: '#A4B0BE' }
              }}
            >
              {field.options?.map((option: any) => (
                <MenuItem
                  key={typeof option === 'string' ? option : option.value}
                  value={typeof option === 'string' ? option : option.value}
                >
                  {typeof option === 'string' ? option : option.label}
                </MenuItem>
              ))}
            </Select>
            {field.helpText && (
              <FormHelperText sx={{ color: '#A4B0BE' }}>{field.helpText}</FormHelperText>
            )}
          </FormControl>
        );

        case 'icon-select':
        return (
          <FormControl key={field.name} fullWidth margin="normal" required={field.required}>
            <InputLabel sx={{ color: '#A4B0BE' }}>{field.label}</InputLabel>
            <Select
              value={value || ''}
              onChange={(e) => handleFieldChange(field.name, e.target.value)}
              label={field.label}
              renderValue={(selected) => {
                if (!selected) return <em>None</em>;
                const selectedIcon = METRIC_ICONS.find(icon => icon.value === selected);
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {selectedIcon && (
                      <Box sx={{ display: 'flex', color: '#3A9188' }}>
                        {React.createElement(selectedIcon.icon)}
                      </Box>
                    )}
                    <span>{selectedIcon?.label || selected}</span>
                  </Box>
                );
              }}
              sx={{
                color: '#ffffff',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#A4B0BE' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#3A9188' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3A9188' },
                '& .MuiSvgIcon-root': { color: '#A4B0BE' }
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 400,
                    '& .MuiMenuItem-root': {
                      '&.category-header': {
                        backgroundColor: '#2C3E50',
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        color: '#3A9188',
                        pointerEvents: 'none',
                        paddingTop: 1.5,
                        paddingBottom: 0.5
                      }
                    }
                  }
                }
              }}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              
              {Object.entries(CATEGORY_LABELS).map(([categoryKey, categoryLabel]) => {
                const iconsInCategory = getIconsByCategory(categoryKey);
                if (iconsInCategory.length === 0) return null;
                
                return [
                  <MenuItem 
                    key={`header-${categoryKey}`} 
                    disabled 
                    className="category-header"
                    sx={{ 
                      backgroundColor: '#2C3E50 !important',
                      opacity: '1 !important'
                    }}
                  >
                    {categoryLabel}
                  </MenuItem>,
                  ...iconsInCategory.map((icon) => (
                    <MenuItem key={icon.value} value={icon.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                        <Box sx={{ display: 'flex', color: '#3A9188', minWidth: 24 }}>
                          {React.createElement(icon.icon,)}
                        </Box>
                        <span>{icon.label}</span>
                      </Box>
                    </MenuItem>
                  ))
                ];
              })}
            </Select>
            {field.helpText && (
              <FormHelperText sx={{ color: '#A4B0BE' }}>{field.helpText}</FormHelperText>
            )}
          </FormControl>
        );

      case 'image-picker':
        return (
          <Box key={field.name} sx={{ my: 2 }}>
            <Typography variant="body2" gutterBottom sx={{ color: '#ffffff' }}>
              {field.label}
            </Typography>
            {value ? (
              <Card sx={{ mb: 1, position: 'relative' }}>
                <CardMedia
                  component="img"
                  height="200"
                  image={value}
                  alt={field.label}
                  sx={{ objectFit: 'cover' }}
                />
                <IconButton
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    bgcolor: 'background.paper',
                    '&:hover': { bgcolor: 'error.light', color: 'white' }
                  }}
                  onClick={() => handleImageRemove(field.name)}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Card>
            ) : null}
            <Button
              variant="outlined"
              startIcon={<ImageIcon />}
              onClick={() => handleImagePickerOpen(field.name)}
              fullWidth
              sx={{
                justifyContent: 'flex-start',
                py: 1.5,
                color: '#A4B0BE',
                borderColor: '#A4B0BE',
                '&:hover': { borderColor: '#3A9188', color: '#3A9188' }
              }}
            >
              {value ? 'Change Image' : 'Choose Image'}
            </Button>
            {field.helpText && (
              <FormHelperText sx={{ color: '#A4B0BE' }}>{field.helpText}</FormHelperText>
            )}
          </Box>
        );

  case 'rich-text':
  return (
    <Box key={field.name} sx={{ my: 3 }}>
      <Typography variant="body2" sx={{ color: '#ffffff', mb: 1 }}>
        {field.label}
      </Typography>
      <RichTextEditor
        value={value}
        onChange={(html) => handleFieldChange(field.name, html)}
      />
      {field.helpText && (
        <Typography variant="caption" sx={{ color: '#A4B0BE' }}>
          {field.helpText}
        </Typography>
      )}
    </Box>
  );

      case 'date':
        return (
          <TextField
            key={field.name}
            fullWidth
            type="date"
            label={field.label}
            value={value}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            required={field.required}
            helperText={field.helpText}
            variant="outlined"
            margin="normal"
            InputLabelProps={{ shrink: true }}
            sx={{
              '& .MuiInputLabel-root': { color: '#A4B0BE' },
              '& .MuiOutlinedInput-root': {
                color: '#ffffff',
                '& fieldset': { borderColor: '#A4B0BE' },
                '&:hover fieldset': { borderColor: '#3A9188' },
                '&.Mui-focused fieldset': { borderColor: '#3A9188' }
              }
            }}
          />
        );
      default:
        return (
          <TextField
            key={field.name}
            fullWidth
            label={field.label}
            value={value}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            required={field.required}
            helperText={field.helpText || `Unknown field type: ${field.type}`}
            variant="outlined"
            margin="normal"
            sx={{
              '& .MuiInputLabel-root': { color: '#A4B0BE' },
              '& .MuiOutlinedInput-root': {
                color: '#ffffff',
                '& fieldset': { borderColor: '#A4B0BE' },
                '&:hover fieldset': { borderColor: '#3A9188' },
                '&.Mui-focused fieldset': { borderColor: '#3A9188' }
              }
            }}
          />
        );
    }
  };

  return (
    <Box>
      {editFields.map((field) => renderField(field))}
      <MediaPicker
        open={mediaPickerOpen}
        onClose={() => setMediaPickerOpen(false)}
        onSelect={handleImageSelect}
        currentValue={currentImageField ? blockData[currentImageField] : undefined}
      />
    </Box>
  );
};

export default BlockFormBuilder;